// Visualization library for the analytics engine. Wraps ApexCharts (loaded
// globally via the layout) plus a sensible "smart default" picker that maps
// a QueryResult shape onto a chart type when the user hasn't picked one.
//
// Invariants every chart keeps (each has a regression test):
//   • every series or slice in one chart has its own colour (chart-colors.ts);
//   • time axes are ordered by bucket value and gap-filled, never by row order (chart-time.ts);
//   • money in different currencies is never stacked or drawn on one axis;
//   • measures in different units never share a stack or a horizontal value axis;
//   • the chart is measured after its title, notes and controls, and redraws on resize and theme change
//     (chart-mount.ts), so legends are never clipped and nothing scrolls inside the chart;
//   • every label uses the column's display settings and the one missing-value label (format.ts).

import type { QueryDisplay, QueryResult, QueryResultColumn } from './types';
import { computeGoalProgress, goalMetaHtml, goalTrackHtml, thresholdState, type Thresholds } from './metric-chrome';
import { chartLabel, currencyDimension, recommendedChart, resultAggregates, resultShapeTraits, rowCurrencyKey, unitSignature, unsupportedReason, type ChartShape } from './chart-guidance';
import { comboLineAliases, gaugeModel, scatterPoints, treemapData, waterfallMeasureOk, waterfallSteps, type WaterfallStep } from './chart-data';
import { displayCell, escapeHtml, formatValue, isAdditive, isCount, missingLabel, resolveFormat, type FormatContext } from './format';
import { renderPivotTable } from './pivot-table';
import { renderSummaryTable, type TableView } from './summary-table';
import { assignSeriesColors, distinctPalette, mixHex } from './chart-colors';
import { cssToken, inkOnFill, readChartTheme, themeColor, toCssColor, type ChartTheme } from './chart-theme';
import { availableChartHeight, chartInstance, mountApex, type ApexInstance, type MountedChart } from './chart-mount';
import { chronological, emptyBucketValue, fillTimeGaps, isTimeColumn, spansYears, timeAxis, timeBuckets, timeValue } from './chart-time';

export { formatValue } from './format';
export { tableMarkup } from './summary-table';
export { awaitApex } from './chart-mount';
export { expandPalette, assignSeriesColors } from './chart-colors';

export type ChartKind =
    | 'bar'
    | 'column'
    | 'stackedColumn'
    | 'line'
    | 'area'
    | 'donut'
    | 'pie'
    | 'kpi'
    | 'table'
    | 'funnel'
    | 'pivot'
    | 'heatmap'
    | 'combo'
    | 'scatter'
    | 'gauge'
    | 'waterfall'
    | 'treemap';

/** Longest-token-first so stackedColumn wins over column, etc. */
const CHART_KIND_PREFIXES: ChartKind[] = [
    'stackedColumn', 'bar', 'column', 'donut', 'line', 'area', 'heatmap',
    'funnel', 'pivot', 'table', 'pie', 'kpi', 'combo', 'scatter', 'gauge', 'waterfall', 'treemap',
];

/** Map persisted ChartType (or legacy dev-seed values like pie-100) → ChartKind. */
export function normalizeChartKind(raw: string | null | undefined): ChartKind | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (CHART_KIND_PREFIXES.includes(trimmed as ChartKind)) return trimmed as ChartKind;
    for (const kind of CHART_KIND_PREFIXES) {
        if (trimmed.startsWith(`${kind}-`)) return kind;
    }
    return null;
}

const PALETTE_VARS = [
    '--chart-1', '--chart-2', '--chart-3', '--chart-4',
    '--chart-5', '--chart-6', '--chart-7', '--chart-8',
    '--chart-9', '--chart-10', '--chart-11', '--chart-12',
    '--chart-13', '--chart-14', '--chart-15', '--chart-16',
];

/** The preset's categorical palette, without near-duplicates (a preset accent equal to a fixed slot). */
function readPalette(): string[] {
    const out = PALETTE_VARS.map(cssToken).filter(Boolean).map(toCssColor);
    if (out.length === 0) out.push(themeColor('--color-primary', '--primary'));
    return distinctPalette(out);
}

/** Distinct colours for these series or slice names, stable per name across charts. */
function seriesColors(names: string[]): string[] {
    return assignSeriesColors(names, readPalette(), toCssColor);
}

/** Area-fill opacity stops from design tokens (same hue, opacity only). */
function areaFill(colors: string[]): Record<string, unknown> {
    const parse = (name: string, fallback: number): number => {
        const n = Number.parseFloat(cssToken(name));
        return Number.isFinite(n) ? n : fallback;
    };
    return {
        type: 'gradient',
        gradient: {
            type: 'vertical', shade: 'light', shadeIntensity: 0, gradientToColors: colors, inverseColors: false,
            opacityFrom: parse('--chart-area-opacity-from', 0.28), opacityTo: parse('--chart-area-opacity-to', 0), stops: [0, 100],
        },
    };
}

export interface RenderOptions {
    chartKind?: ChartKind | null;
    /** Start the value axis at zero for non-negative series (admin metric trends). */
    zeroBaseline?: boolean;
    /** Never offer the zoom/pan toolbar, however many points the trend has. */
    noZoom?: boolean;
    /** The surrounding tile's title; a chart title equal to it is not repeated. */
    title?: string;
    showSparkline?: boolean;
    comparisonDelta?: number | null;
    comparisonLabel?: string | null;
    goal?: number | null;
    thresholds?: Thresholds | null;
    onDrill?: (categoryKey: string) => void;
    sourceEntitySlug?: string | null;
    /** Saved presentation settings (QuerySpec.display). */
    display?: QueryDisplay | null;
    /** The builder's preview row cap; hitting it is not the saved report's result limit. */
    previewRowCap?: number;
    /** The host already displays a prominent partial-results notice. */
    hasPartialResultsNotice?: boolean;
    /** Authors persist widths; viewers may resize without changing the saved report. */
    onColumnResize?: (alias: string, widthCh: number) => void;
    /** The report's sort, so pivot axes sorted on a grouping field follow it. */
    sort?: Array<{ by: string; dir: 'asc' | 'desc' }> | null;
    /**
     * False for measures whose group values must not be added again on the client (dataset measures that are not
     * reaggregable). Server totals are always used when the result carries them.
     */
    reaggregable?: (measure: QueryResultColumn) => boolean;
    /** Heading level for the chart title (default 3). */
    headingLevel?: number;
    /**
     * A table's page position and sort, kept by a host that repaints the same result (the builder preview), so a
     * repaint leaves the reader on their page. Updated in place as the reader pages and sorts.
     */
    tableView?: TableView;
}

/**
 * Viewers and dashboard tiles never receive the report spec, so the result's own display metadata stands in:
 * its sort orders pivot axes, and a measure the server marks non-reaggregable is never re-added on the client.
 * Options the caller passes win.
 */
export function withResultMetadata(result: QueryResult, options: RenderOptions): RenderOptions {
    const sort = options.sort ?? (result.sort?.length ? result.sort.map(s => ({ by: s.by, dir: s.direction })) : null);
    const caller = options.reaggregable;
    const reaggregable = (measure: QueryResultColumn) => measure.reaggregable !== false && (caller?.(measure) ?? true);
    return { ...options, sort, reaggregable };
}

const GRAPHIC_KINDS: ReadonlySet<ChartKind> = new Set(['bar', 'column', 'stackedColumn', 'line', 'area', 'donut', 'pie', 'heatmap', 'combo', 'scatter', 'waterfall', 'treemap']);
/** Drawn by the chart library: measured after the title and notes, and saved as an image. Gauges print their values. */
const APEX_KINDS: ReadonlySet<ChartKind> = new Set([...GRAPHIC_KINDS, 'gauge']);

/**
 * True when a table of `result` shows values the drawn visual does not already print: graphic charts
 * and KPI trend sparklines. Single numbers, funnels (stage counts are printed), tables and pivots are not.
 */
export function offersTableView(result: QueryResult, options: RenderOptions = {}): boolean {
    if (result.rows.length === 0 || result.reportType === 'funnel') return false;
    const kind = resolveChart(result, options).kind;
    if (kind === 'kpi') return !!options.showSparkline && result.rows.length > 1
        && result.columns.filter(c => c.kind === 'measure').length === 1;
    return GRAPHIC_KINDS.has(kind);
}

/** True when `result` draws a chart that can be saved as an image (every chart-library visual, gauges included). */
export function drawsChartImage(result: QueryResult, options: RenderOptions = {}): boolean {
    return result.rows.length > 0 && result.reportType !== 'funnel' && APEX_KINDS.has(resolveChart(result, options).kind);
}

/** Category keys whose group holds at least one record the viewer can open; empty groups never drill. */
export function drillableKeys(result: QueryResult): string[] {
    if (!result.runDescriptor || result.reportType === 'recordList' || !result.categoryKeys?.length) return [];
    if (result.eligibleRecordCount === 0 || result.outcome === 'empty') return [];
    if (result.reportType === 'funnel')
        return result.categoryKeys.filter((_, i) => Number(result.funnel?.[i]?.count ?? 0) > 0);
    // Gap-filled buckets carry no key: there are no records behind them.
    return result.categoryKeys.slice(0, result.rows.length).filter(key => !!key);
}

/**
 * What a chart sees. `reduced` leaves out the engine's currency split, which charts draw as separate
 * panels; pivots and tables keep it as an ordinary column.
 */
function shapeOf(result: QueryResult, reduced = true, display: QueryDisplay | null = null): ChartShape {
    const currency = reduced ? currencyDimension(result) : null;
    const dims = result.columns.filter(c => c.kind === 'dimension' && c !== currency);
    const axis = dims[0];
    let categories: number | undefined;
    if (axis) {
        const buckets = isTimeColumn(axis) ? timeBuckets({ ...result, columns: result.columns.filter(c => c !== currency) }) : null;
        categories = buckets ? buckets.values.length : new Set(result.rows.map(row => categoryKey(row[axis.alias]))).size;
    }
    return {
        reportType: result.reportType,
        dimensions: dims.length,
        measures: result.columns.filter(c => c.kind === 'measure').length,
        ...(categories === undefined ? {} : { categories }),
        ...resultShapeTraits(result),
        aggregates: resultAggregates(result),
        ...(display?.target != null && Number.isFinite(display.target) ? { target: true } : {}),
    };
}

/**
 * The chart to draw. The author's choice wins when it can show this result; otherwise the
 * recommended chart is drawn and `note` explains the substitution.
 */
export function resolveChart(result: QueryResult, options: RenderOptions = {}): { kind: ChartKind; note?: string } {
    const display = options.display ?? result.display ?? null;
    const full = shapeOf(result, false, display);
    const shape = result.reportType === 'pivot' ? full : shapeOf(result, true, display);
    const explicit = normalizeChartKind(options.chartKind ?? undefined);
    if (!explicit) return { kind: recommendedChart(shape) };
    // Dashboard KPI tiles may carry a trend for their sparkline.
    if (explicit === 'kpi' && options.showSparkline && shape.measures === 1) return { kind: explicit };
    const reason = unsupportedReason(explicit, explicit === 'pivot' || explicit === 'table' ? full : shape);
    if (!reason) return { kind: explicit };
    const kind = recommendedChart(shape);
    return { kind, note: `Shown as ${chartLabel(kind).toLowerCase()}: the ${chartLabel(explicit).toLowerCase()} chart cannot show this report. ${reason}` };
}

export function pickDefaultChart(result: QueryResult, options: RenderOptions = {}): ChartKind {
    return resolveChart(result, options).kind;
}

function truncationNote(result: QueryResult, options: RenderOptions = {}): string | null {
    if (!result.truncated) return null;
    if (result.reportType === 'recordList' && options.previewRowCap != null && result.rows.length <= options.previewRowCap)
        return `The preview shows the first ${result.rows.length.toLocaleString()} records. The report's result limit is higher.`;
    return result.reportType === 'recordList'
        ? `Showing the first ${result.rows.length.toLocaleString()} records; more records match. Raise the result limit or add filters to see the rest.`
        : `Showing the first ${result.rows.length.toLocaleString()} groups; more groups match, so totals by group are incomplete. Raise the result limit or narrow the report.`;
}

function sameTitle(a: string | null | undefined, b: string | null | undefined): boolean {
    return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Title, substitution and partial-result notes sit above the visual. */
function prependNotes(host: HTMLElement, result: QueryResult, options: RenderOptions, note?: string): void {
    const notes = [note, options.hasPartialResultsNotice ? null : truncationNote(result, options)].filter((text): text is string => !!text)
        .map(text => `<p class="analytics-chart-note" role="note">${escapeHtml(text)}</p>`);
    const titleText = options.display?.title;
    // A tile already headed with the same words does not repeat them.
    const level = Math.min(6, Math.max(2, options.headingLevel ?? 3));
    const title = titleText && !sameTitle(titleText, options.title) ? `<p class="analytics-chart-title" role="heading" aria-level="${level}">${escapeHtml(titleText)}</p>` : '';
    if (title || notes.length) host.insertAdjacentHTML('afterbegin', title + notes.join(''));
}

export interface RenderedChart {
    destroy(): void;
}

/**
 * Render `result` into `host`, replacing any previous content. Returns a
 * handle the caller can dispose to clean up the underlying Apex instance.
 */
export function renderChart(host: HTMLElement, result: QueryResult, options: RenderOptions = {}): RenderedChart {
    options = withResultMetadata(result, options);
    const resolved = resolveChart(result, options);
    const note = result.rows.length === 0 && result.reportType !== 'funnel' ? undefined : resolved.note;
    if (APEX_KINDS.has(resolved.kind) && result.rows.length > 0) {
        // Notes go in first so the chart is measured with them in place.
        host.innerHTML = '';
        prependNotes(host, result, options, note);
        return renderChartBody(host, result, options, resolved.kind);
    }
    const rendered = renderChartBody(host, result, options, resolved.kind);
    prependNotes(host, result, options, note);
    return rendered;
}

function renderChartBody(host: HTMLElement, result: QueryResult, options: RenderOptions, kind: ChartKind): RenderedChart {
    const display = options.display ?? null;

    if (result.rows.length === 0 && result.reportType !== 'funnel') {
        paintChartEmpty(host, 'No data for this query.', 'Adjust filters or pick a different date range.');
        return { destroy() {} };
    }

    const draw = (): RenderedChart => { switch (kind) {
        case 'kpi':       return renderKpi(host, result, options);
        case 'gauge':     return renderGauge(host, result, options);
        case 'table':     return renderTable(host, result, display, options.onColumnResize, options.reaggregable, options.tableView);
        case 'funnel':    return renderFunnel(host, result, display);
        case 'pivot':     return renderPivot(host, result, display, options);
        default:          return renderGraphic(host, result, kind, options);
    } };
    const rendered = draw();
    const keys = options.onDrill ? drillableKeys(result) : [];
    if (keys.length) {
        if (kind === 'kpi' && result.categoryKeys!.length === 1 && host.firstElementChild instanceof HTMLElement) {
            const surface = host.firstElementChild; surface.tabIndex = 0; surface.setAttribute('role', 'button');
            surface.setAttribute('aria-label', `${surface.textContent?.trim()}. View contributing records`);
            const click = () => options.onDrill!(keys[0]);
            const keydown = (event: KeyboardEvent) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); click(); } };
            surface.addEventListener('click', click); surface.addEventListener('keydown', keydown);
            return { destroy() { surface.removeEventListener('click', click); surface.removeEventListener('keydown', keydown); rendered.destroy(); } };
        }
        if (typeof document === 'undefined' || typeof document.createElement !== 'function' || typeof host.appendChild !== 'function') return rendered;
        const controls = drillControls(result, keys, display, options.onDrill!);
        host.appendChild(controls);
        return { destroy() { rendered.destroy(); controls.remove(); } };
    }
    return rendered;
}

let drillListSeq = 0;
/** "View records" for a chosen group: a list for a few groups, a search for many (never thousands of options). */
function drillControls(result: QueryResult, keys: string[], display: QueryDisplay | null, onDrill: (key: string) => void): HTMLElement {
    const controls = document.createElement('div'); controls.className = 'analytics-drill-controls';
    const dimensions = result.columns.filter(c => c.kind === 'dimension');
    const labelFor = (key: string) => {
        const index = result.categoryKeys!.indexOf(key);
        const row = result.rows[index];
        return dimensions.map(c => displayCell(row?.[c.alias], c, display, { row })).join(' · ') || 'All contributing records';
    };
    const button = document.createElement('button'); button.type = 'button'; button.textContent = 'View records';
    if (keys.length <= 25) {
        const select = document.createElement('select'); select.setAttribute('aria-label', 'Report group');
        keys.forEach(key => { const option = document.createElement('option'); option.value = key; option.textContent = labelFor(key); select.appendChild(option); });
        if (keys.length > 1) controls.appendChild(select);
        button.addEventListener('click', () => onDrill(select.value));
        controls.appendChild(button);
        return controls;
    }
    // Many groups: a searchable field whose suggestions are filtered and capped as the viewer types.
    const labels = new Map<string, string>();
    for (const key of keys) {
        const base = labelFor(key); let label = base;
        for (let n = 2; labels.has(label); n++) label = `${base} (${n})`;
        labels.set(label, key);
    }
    const listId = `analytics-drill-groups-${++drillListSeq}`;
    const input = document.createElement('input'); input.type = 'search'; input.setAttribute('aria-label', 'Report group');
    input.placeholder = `Search ${keys.length.toLocaleString()} groups`; input.setAttribute('list', listId); input.autocomplete = 'off';
    const list = document.createElement('datalist'); list.id = listId;
    const status = document.createElement('span'); status.setAttribute('role', 'status'); status.className = 'text-muted small';
    const fill = () => {
        const query = input.value.trim().toLowerCase();
        const matches: string[] = [];
        for (const label of labels.keys()) { if (!query || label.toLowerCase().includes(query)) matches.push(label); if (matches.length >= 50) break; }
        list.replaceChildren(...matches.map(label => { const option = document.createElement('option'); option.value = label; return option; }));
    };
    input.addEventListener('input', fill); fill();
    button.addEventListener('click', () => {
        const key = labels.get(input.value);
        if (key) { status.textContent = ''; onDrill(key); } else status.textContent = 'Choose a group from the suggestions.';
    });
    controls.append(input, list, button, status);
    return controls;
}

function paintChartEmpty(host: HTMLElement, title: string, hint: string): void {
    host.innerHTML = `<div class="analytics-widget__empty">
        <i class="bi bi-inbox analytics-widget__empty-icon" aria-hidden="true"></i>
        <div class="analytics-widget__empty-title">${escapeHtml(title)}</div>
        <div class="analytics-widget__empty-hint text-muted small">${escapeHtml(hint)}</div>
    </div>`;
}

/** A paragraph of explanation inside the visual (graphic kinds only, so the host is a real element). */
function noteElement(text: string): HTMLElement {
    const p = document.createElement('p'); p.className = 'analytics-chart-note'; p.setAttribute('role', 'note'); p.textContent = text;
    return p;
}

// ─────────────────────────── KPI ───────────────────────────

/** The measure over time, oldest first, with empty buckets filled; latest and previous buckets for the headline and delta. */
export function kpiTrend(result: QueryResult): { rows: Array<Record<string, unknown>>; latest: Record<string, unknown> | null; previous: Record<string, unknown> | null } {
    const ordered = timeAxis(result) ? fillTimeGaps(result) : result;
    const axis = timeAxis(ordered);
    // Rows without a date cannot be "latest".
    const rows = axis ? ordered.rows.filter(row => timeValue(row[axis.alias]) != null) : ordered.rows;
    return { rows, latest: rows[rows.length - 1] ?? null, previous: rows[rows.length - 2] ?? null };
}

/** Latest minus previous bucket, chronologically; null when either is missing. */
export function kpiDelta(result: QueryResult): number | null {
    const measure = result.columns.find(c => c.kind === 'measure');
    if (!measure) return null;
    const { latest, previous } = kpiTrend(result);
    const a = chartNumber(latest?.[measure.alias]), b = chartNumber(previous?.[measure.alias]);
    return a == null || b == null ? null : a - b;
}

function lowerIsBetter(thresholds?: Thresholds | null): boolean {
    if (!thresholds) return false;
    if (thresholds.direction) return thresholds.direction === 'lowerIsBetter';
    return thresholds.warn != null && thresholds.danger != null && thresholds.danger > thresholds.warn;
}

function renderKpi(host: HTMLElement, result: QueryResult, options: RenderOptions = {}): RenderedChart {
    const measures = result.columns.filter(c => c.kind === 'measure');
    const measure = measures[0];
    if (!measure || result.rows.length === 0) {
        paintChartEmpty(host, 'No measure to display.', 'Add a measure to this query.');
        return { destroy() {} };
    }
    const kpiDisplay = options.display ?? result.display ?? null;
    const currency = currencyDimension(result);
    const otherDims = result.columns.filter(c => c.kind === 'dimension' && c !== currency);
    if (measures.length === 1 && !options.showSparkline && currency && otherDims.length === 0 && result.rows.length > 1) {
        // One total per currency: amounts in different currencies are never added or shown as one number.
        const figures = result.rows.map(row => {
            const value = escapeHtml(displayCell(row[measure.alias], measure, kpiDisplay, { row }));
            const caption = escapeHtml(`${measure.label ?? measure.alias} (${missingLabel(row[currency.alias]) ?? String(row[currency.alias])})`);
            return `<div class="analytics-kpi__figure"><div class="kpi-value tabular-nums">${value}</div>
            <div class="kpi-label text-muted small">${caption}</div></div>`;
        }).join('');
        host.innerHTML = `<div class="analytics-kpi analytics-kpi--rich analytics-kpi--multi" role="group" aria-label="${escapeHtml(measure.label ?? measure.alias)} by currency">${figures}</div>`;
        return { destroy() {} };
    }
    if (measures.length !== 1 || (!options.showSparkline && result.rows.length !== 1)) return renderTable(host, result, kpiDisplay, options.onColumnResize, options.reaggregable, options.tableView);
    const showSparkline = !!options.showSparkline && result.rows.length > 1;
    // The headline is the latest period, chronologically, never whichever row arrived first.
    const trend = showSparkline ? kpiTrend(result) : null;
    const headlineRow = trend?.latest ?? result.rows[0];
    const value = headlineRow[measure.alias];
    const numValue = chartNumber(value);
    const format = (n: number) => formatValue(n, measure, kpiDisplay, { row: headlineRow });
    const display = displayCell(value, measure, kpiDisplay, { row: headlineRow });
    const comparison = options.comparisonDelta;
    const comparisonLabel = options.comparisonLabel ?? 'vs last period';
    const better = comparison == null || comparison === 0 ? null : (comparison > 0) !== lowerIsBetter(options.thresholds);
    const deltaHtml = comparison == null ? '' :
        `<span class="analytics-widget__delta small ${better == null ? 'text-muted' : better ? 'text-success' : 'text-danger'}">
            <i class="bi ${comparison > 0 ? 'bi-arrow-up-right' : comparison < 0 ? 'bi-arrow-down-right' : 'bi-arrow-right'}" aria-hidden="true"></i>
            <span class="sr-only">${comparison > 0 ? 'Up' : comparison < 0 ? 'Down' : 'No change'}</span>
            ${escapeHtml(format(Math.abs(comparison)))}
            <span class="text-muted">${escapeHtml(comparisonLabel)}</span>
        </span>`;
    const goal = options.goal;
    const progress = goal != null && Number.isFinite(goal) && numValue != null ? computeGoalProgress(numValue, goal, format) : null;
    const goalHtml = progress ? `${goalTrackHtml(progress)}${goalMetaHtml(progress)}` : '';
    const state = thresholdState(numValue, options.thresholds);
    const toneClass = state ? `analytics-kpi--${state}` : '';
    const axis = trend ? result.columns.find(c => c.kind === 'dimension') : undefined;
    const periodHtml = trend && axis ? `<div class="kpi-label analytics-kpi__period small">${escapeHtml(displayCell(headlineRow[axis.alias], axis, kpiDisplay))}</div>` : '';
    host.innerHTML = `
        <div class="analytics-kpi analytics-kpi--rich${toneClass ? ` ${toneClass}` : ''}">
            <div class="kpi-value tabular-nums">${escapeHtml(display)}</div>
            <div class="kpi-label text-muted small">${escapeHtml(measure.label ?? measure.alias)}</div>
            ${periodHtml}
            ${deltaHtml}
            ${goalHtml}
            ${showSparkline ? '<div class="analytics-kpi__sparkline mt-2" data-kpi-sparkline></div>' : ''}
        </div>`;
    let sparkline: MountedChart | null = null;
    const sparkEl = showSparkline && typeof host.querySelector === 'function' ? host.querySelector<HTMLElement>('[data-kpi-sparkline]') : null;
    if (sparkEl && trend && axis) {
        const data = trend.rows.map(row => chartNumber(row[measure.alias]));
        const withYear = spansYears(trend.rows.map(row => row[axis.alias]));
        const labels = trend.rows.map(row => displayCell(row[axis.alias], axis, kpiDisplay, { withYear }));
        const color = seriesColors([measure.label ?? measure.alias])[0];
        sparkEl.dataset.analyticsChartMount = '';
        sparkline = mountApex(sparkEl, ({ theme }) => ({
            chart: { type: 'area', sparkline: { enabled: true }, height: 48, animations: { enabled: false }, parentHeightOffset: 0, foreColor: theme.foreColor },
            series: [{ name: measure.label ?? measure.alias, data }],
            stroke: { curve: 'straight', width: 2 },
            fill: areaFill([color]),
            colors: [color],
            tooltip: { theme: theme.mode, x: { show: true, formatter: (_: unknown, point: { dataPointIndex?: number }) => escapeHtml(labels[point?.dataPointIndex ?? 0] ?? '') },
                y: { title: { formatter: () => '' }, formatter: (_v: number, point: { dataPointIndex?: number }) => {
                    const row = trend.rows[point?.dataPointIndex ?? 0];
                    return escapeHtml(displayCell(row?.[measure.alias], measure, kpiDisplay, { row }));
                } } },
        }), { host: sparkEl, height: () => 48, label: `Trend of ${measure.label ?? measure.alias} from ${labels[0]} to ${labels[labels.length - 1]}` });
    }
    return { destroy() { sparkline?.destroy(); } };
}

// ─────────────────────────── Table ───────────────────────────

function renderTable(host: HTMLElement, result: QueryResult, display: QueryDisplay | null = null, onResize?: RenderOptions['onColumnResize'],
    reaggregable?: RenderOptions['reaggregable'], tableView?: TableView): RenderedChart {
    return renderSummaryTable(host, result, display, onResize, reaggregable, tableView);
}

/** A table fallback inside a graphic host keeps the title and notes already above it. */
function renderTableInto(host: HTMLElement, result: QueryResult, options: RenderOptions, note?: string): RenderedChart {
    const slot = document.createElement('div');
    if (note) host.appendChild(noteElement(note));
    host.appendChild(slot);
    const table = renderSummaryTable(slot, result, options.display ?? null, options.onColumnResize, options.reaggregable, options.tableView);
    return { destroy() { table.destroy(); slot.remove(); } };
}

/** @deprecated Colours are assigned per chart with assignSeriesColors; kept for callers that colour one key. */
export function stableCategoryColor(key: string, palette: string[]): string {
    return assignSeriesColors([key], palette)[0];
}

// ─────────────────────────── Funnel ───────────────────────────

const FUNNEL_COUNT: QueryResultColumn = { alias: 'count', label: 'Records', kind: 'measure', dataType: 'number', unit: 'count', aggregate: 'count', fieldKind: 'number' };

function renderFunnel(host: HTMLElement, result: QueryResult, display: QueryDisplay | null = null): RenderedChart {
    display ??= result.display ?? null;
    const stages = result.funnel ?? [];
    if (stages.length === 0) {
        paintChartEmpty(host, 'No funnel stages.', 'Configure funnel stages on this report.');
        return { destroy() {} };
    }
    const colours = seriesColors(stages.map(s => s.stage));
    // A stage distribution: widths are relative to the largest stage, which need not be the first.
    const top = Math.max(...stages.map(s => Number(s.count) || 0), 0) || 1;
    const rows = stages
        .map((s, idx) => {
            const width = Math.max(18, Math.min(100, ((Number(s.count) || 0) / top) * 100));
            const conv = idx === 0 || s.conversionFromPrev == null ? '' :
                ` <span>${(s.conversionFromPrev * 100).toFixed(1)}% of previous stage</span>`;
            return `
                <div class="analytics-funnel-row" style="width:${width.toFixed(2)}%">
                    <div class="analytics-funnel-row__head">
                        <strong>${escapeHtml(s.stage)}</strong>
                        <span class="tabular-nums">${escapeHtml(formatValue(s.count, FUNNEL_COUNT, display))}${conv}</span>
                    </div>
                    <div class="analytics-funnel-bar" style="width:100%; background:${colours[idx]}"></div>
                </div>`;
        })
        .join('');
    host.innerHTML = `<p>Current stage distribution</p><div class="analytics-funnel">${rows}</div>`;
    return { destroy() {} };
}

// ─────────────────────────── Pivot ───────────────────────────

function renderPivot(host: HTMLElement, result: QueryResult, display: QueryDisplay | null, options: RenderOptions): RenderedChart {
    return renderPivotTable(host, result, display, () => renderTable(host, result, display, options.onColumnResize, options.reaggregable, options.tableView),
        { sort: options.sort, reaggregable: options.reaggregable });
}

// ─────────────────────────── Graphic charts ───────────────────────────

interface SeriesContext {
    /** The one currency every value in this chart is in. */
    currency?: string | null;
    /** Lazily measured height, for panels the caller sizes. */
    height?: () => number;
    /** Element whose resizes redraw the chart (defaults to the chart's host). */
    observe?: HTMLElement;
}

/**
 * Money split by currency is drawn per currency: one panel per currency, each on its own axis with the
 * currency in its legend and ticks. A single currency is drawn as one chart labelled with it.
 */
function renderGraphic(host: HTMLElement, result: QueryResult, kind: ChartKind, options: RenderOptions): RenderedChart {
    // Money split by a currency dimension, or text-stored money whose rows carry their own (hidden) currency code.
    const currencyOf = rowCurrencyKey(result);
    if (!currencyOf) return renderSeries(host, result, kind, options, {});
    const currency = currencyDimension(result);
    const reduced: QueryResult = currency ? { ...result, columns: result.columns.filter(c => c !== currency) } : result;
    const codes: unknown[] = [];
    for (const row of result.rows) if (!codes.some(code => categoryKey(code) === categoryKey(currencyOf(row)))) codes.push(currencyOf(row));
    // A hidden code is empty when a group has no amounts (a gap-filled period, an empty total): such rows hold no
    // money in any currency, so they belong to every panel rather than forming a "(No value)" currency of their own.
    const floating = (row: Record<string, unknown>) => !currency && currencyOf(row) == null;
    if (!currency && codes.some(code => code != null)) codes.splice(0, codes.length, ...codes.filter(code => code != null));
    if (codes.length <= 1) return renderSeries(host, reduced, kind, options, { currency: codes[0] == null ? null : String(codes[0]) });
    if (!reduced.columns.some(c => c.kind === 'dimension'))
        return renderTableInto(host, result, options, 'Totals in different currencies are listed separately: they cannot be added together or share an axis.');
    const grid = document.createElement('div'); grid.className = 'analytics-chart-multiples';
    // Structural layout is set here so panels never overlap even before the stylesheet loads.
    grid.style.display = 'grid'; grid.style.gap = 'var(--space-3)'; grid.style.minWidth = '0';
    grid.setAttribute('role', 'group'); grid.setAttribute('aria-label', 'Totals by currency');
    host.appendChild(grid);
    const panels = codes.map(code => {
        const panel = document.createElement('section'); panel.className = 'analytics-chart-multiple';
        const label = missingLabel(code) ?? String(code);
        const heading = document.createElement('p'); heading.className = 'analytics-chart-multiple__title'; heading.textContent = label;
        heading.id = `analytics-currency-${Math.random().toString(36).slice(2)}`; panel.setAttribute('aria-labelledby', heading.id);
        const body = document.createElement('div'); body.className = 'analytics-chart-multiple__body';
        panel.append(heading, body); grid.appendChild(panel);
        return { code, label, body, heading };
    });
    // Panels share the host's height in a grid sized to its width.
    const panelHeight = (): number => {
        const columns = Math.max(1, Math.min(panels.length, Math.floor(((host.clientWidth || 640) + 16) / 300)));
        grid.style.gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;
        const rows = Math.ceil(panels.length / columns);
        const titles = panels[0].heading.offsetHeight + 8;
        const available = availableChartHeight(host, grid, 320 * rows);
        const height = Math.max(160, Math.floor((available - rows * titles - (rows - 1) * 12) / rows));
        for (const p of panels) p.body.style.height = `${height}px`;
        return height;
    };
    const rendered = panels.map(panel => {
        const indices = result.rows.map((row, i) => floating(row) || categoryKey(currencyOf(row)) === categoryKey(panel.code) ? i : -1).filter(i => i >= 0);
        const subset: QueryResult = { ...reduced, rows: indices.map(i => result.rows[i]),
            categoryKeys: result.categoryKeys ? indices.map(i => result.categoryKeys![i]) : undefined,
            cellHtml: result.cellHtml ? indices.map(i => result.cellHtml![i]) : result.cellHtml };
        return renderSeries(panel.body, subset, kind, options, { currency: panel.code == null ? null : String(panel.code), height: panelHeight, observe: host });
    });
    return { destroy() { rendered.forEach(r => r.destroy()); grid.remove(); } };
}

function renderSeries(host: HTMLElement, result: QueryResult, kind: ChartKind, options: RenderOptions, context: SeriesContext): RenderedChart {
    if (kind === 'heatmap') return renderHeatmap(host, result, options, context);
    if (kind === 'scatter') return renderScatter(host, result, options, context);
    if (kind === 'waterfall') return renderWaterfall(host, result, options, context);
    if (kind === 'treemap') return renderTreemap(host, result, options, context);
    return renderApex(host, result, kind, options, context);
}

/** Kinds that page through more than 50 categories. Trends are paged too unless the axis is time (ANX-0511). */
const PAGED_KINDS: ReadonlySet<ChartKind> = new Set(['bar', 'column', 'stackedColumn', 'combo', 'line', 'area']);

/**
 * Categorical charts, trends over named groups included, page through large group sets; a trend over time zooms
 * instead (a long one draws a downsampled line), and circular charts cap slices.
 */
function renderApex(host: HTMLElement, result: QueryResult, kind: ChartKind, options: RenderOptions, context: SeriesContext): RenderedChart {
    const pageSize = 25;
    // A category and all its split series must stay in the same window.
    // Slicing result rows can silently cut half of a category's values away.
    const category = result.columns.find(c => c.kind === 'dimension');
    const categoryRows = new Map<string, number[]>();
    result.rows.forEach((row, index) => {
        const key = category ? categoryKey(row[category.alias]) : String(index);
        const indices = categoryRows.get(key) ?? [];
        indices.push(index); categoryRows.set(key, indices);
    });
    const groups = [...categoryRows.values()];
    if (groups.length <= 50 || !PAGED_KINDS.has(kind) || (category && isTimeColumn(category))) return renderApexPage(host, result, kind, options, context);
    const controls = document.createElement('div'); controls.className = 'analytics-chart-navigation';
    controls.setAttribute('role', 'group'); controls.setAttribute('aria-label', 'Chart groups');
    const previous = document.createElement('button'); previous.type = 'button'; previous.className = 'sp-button sp-button--neutral sp-button--sm'; previous.textContent = 'Previous'; previous.setAttribute('aria-label', 'Previous chart groups');
    const next = document.createElement('button'); next.type = 'button'; next.className = previous.className; next.textContent = 'Next'; next.setAttribute('aria-label', 'Next chart groups');
    const status = document.createElement('span'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    controls.append(previous, status, next);
    const viewport = document.createElement('div'); viewport.className = 'analytics-chart-page';
    // Keep a bounded chart window while the native scrollbar spans every loaded group.
    // Rendering a single SVG hundreds of thousands of pixels wide exhausts browser limits.
    const scrollbar = document.createElement('div'); scrollbar.className = 'analytics-chart-scrollbar';
    scrollbar.tabIndex = 0; scrollbar.setAttribute('role', 'region');
    scrollbar.setAttribute('aria-label', 'Scroll through chart groups');
    scrollbar.title = 'Scroll horizontally to explore groups. Use Left and Right, or Home and End.';
    const track = document.createElement('div'); track.className = 'analytics-chart-scrollbar__track'; scrollbar.append(track);
    host.append(controls, viewport, scrollbar);
    let offset = 0;
    let disposed = false;
    const lastOffset = Math.floor((groups.length - 1) / pageSize) * pageSize;
    let current: RenderedChart | null = null;
    let viewportHeight = 0;
    const fitViewport = (): number => {
        context.height?.();
        viewportHeight = availableChartHeight(host, viewport);
        viewport.style.height = `${viewportHeight}px`;
        return viewportHeight;
    };
    const showPage = () => {
        if (disposed) return;
        current?.destroy(); viewport.replaceChildren();
        fitViewport();
        const end = Math.min(offset + pageSize, groups.length);
        status.textContent = `Groups ${(offset + 1).toLocaleString()}–${end.toLocaleString()} of ${groups.length.toLocaleString()}${result.truncated ? ' loaded' : ''}`;
        previous.disabled = offset === 0; next.disabled = end === groups.length;
        const indices = groups.slice(offset, end).flat();
        current = renderApexPage(viewport, { ...result, rows: indices.map(i => result.rows[i]), categoryKeys: result.categoryKeys ? indices.map(i => result.categoryKeys![i]) : undefined },
            kind, options, { ...context, height: () => viewportHeight, observe: viewport });
    };
    const syncScrollbar = () => {
        if (disposed) return;
        track.style.width = `${Math.max(scrollbar.clientWidth + 1, groups.length * 24)}px`;
        scrollbar.scrollLeft = offset / lastOffset * (scrollbar.scrollWidth - scrollbar.clientWidth);
    };
    const goTo = (value: number) => { if (disposed) return; offset = Math.max(0, Math.min(lastOffset, value)); syncScrollbar(); showPage(); };
    previous.addEventListener('click', () => { goTo(offset - pageSize); if (previous.disabled) next.focus(); });
    next.addEventListener('click', () => { goTo(offset + pageSize); if (next.disabled) previous.focus(); });
    let scrollFrame = 0;
    scrollbar.addEventListener('scroll', () => {
        if (disposed) return;
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
            if (disposed) return;
            const range = scrollbar.scrollWidth - scrollbar.clientWidth;
            const nextOffset = range > 0 ? Math.round(scrollbar.scrollLeft / range * lastOffset) : 0;
            if (offset !== nextOffset) { offset = nextOffset; showPage(); }
        });
    });
    scrollbar.addEventListener('keydown', event => {
        const target = event.key === 'Home' ? 0 : event.key === 'End' ? lastOffset
            : event.key === 'ArrowLeft' ? offset - 1 : event.key === 'ArrowRight' ? offset + 1
            : event.key === 'PageUp' ? offset - pageSize : event.key === 'PageDown' ? offset + pageSize : null;
        if (target !== null) { event.preventDefault(); goTo(target); }
    });
    let resizeTimer = 0;
    const resize = new ResizeObserver(() => {
        syncScrollbar();
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            if (disposed) return;
            const before = viewportHeight;
            fitViewport();
            if (Math.abs(viewportHeight - before) >= 4) showPage();
        }, 150) as unknown as number;
    });
    resize.observe(scrollbar); resize.observe(context.observe ?? host);
    // Notes and drill controls are appended by renderChart after this function returns.
    const firstFrame = requestAnimationFrame(() => { syncScrollbar(); showPage(); });
    return { destroy() { disposed = true; clearTimeout(resizeTimer); cancelAnimationFrame(firstFrame); cancelAnimationFrame(scrollFrame); resize.disconnect(); current?.destroy(); controls.remove(); viewport.remove(); scrollbar.remove(); } };
}

/** Shares of a whole: the largest slices, the rest combined into Other when the measure adds up. */
export function pieSlices(result: QueryResult, display: QueryDisplay | null = null, limit = 10): {
    labels: string[]; values: number[]; rows: Array<number | null>; note?: string; error?: string; percentages: boolean;
} {
    const dims = result.columns.filter(c => c.kind === 'dimension');
    const measures = result.columns.filter(c => c.kind === 'measure');
    const empty = { labels: [], values: [], rows: [], percentages: false };
    if (measures.length !== 1) return { ...empty, error: 'Pie and donut charts need exactly one measure. Use the data table.' };
    const measure = measures[0];
    const values = result.rows.map(row => chartNumber(row[measure.alias]));
    if (values.some(v => v == null || v < 0)) return { ...empty, error: 'Pie and donut charts need one non-negative measure with no missing values. Use the data table.' };
    if (values.every(v => v === 0)) return { ...empty, error: 'Every group is zero, so there are no shares to show. Use the data table.' };
    const additive = isAdditive(measure);
    const order = values.map((_, i) => i).sort((a, b) => values[b]! - values[a]! || a - b);
    const label = (index: number) => dims.map(d => displayCell(result.rows[index][d.alias], d, display, { row: result.rows[index] })).join(' · ');
    const notes: string[] = [];
    let kept = order;
    let other: number | null = null;
    if (order.length > limit) {
        if (additive) {
            kept = order.slice(0, limit - 1);
            other = order.slice(limit - 1).reduce((sum, i) => sum + values[i]!, 0);
            notes.push(`The ${(order.length - kept.length).toLocaleString()} smallest groups are combined into Other.`);
        } else {
            kept = order.slice(0, limit);
            notes.push(`Showing the ${limit} largest of ${order.length.toLocaleString()} groups. ${measure.label ?? measure.alias} cannot be added up, so the rest are not combined into Other.`);
        }
    }
    if (result.truncated) notes.push('This result is partial (more groups match), so shares are of the loaded groups only.');
    if (!additive) notes.push(`Slices show ${measure.label ?? measure.alias} for each group, which is not a share of a total.`);
    const labels = kept.map(label); const sliceValues = kept.map(i => values[i]!); const rows: Array<number | null> = [...kept];
    if (other != null) { labels.push('Other'); sliceValues.push(other); rows.push(null); }
    return { labels, values: sliceValues, rows, note: notes.join(' ') || undefined, percentages: additive };
}

/** The most points a trend over time draws; a longer one draws this many representative points (ANX-0511, 0514). */
export const MAX_TREND_POINTS = 1000;

/**
 * Largest-Triangle-Three-Buckets downsampling: the indexes of `threshold` points that keep the shape of the line
 * (peaks, troughs, the first and the last point). Missing values count as zero when choosing. Returns every index
 * when the series is short enough.
 */
export function downsampleIndices(values: ReadonlyArray<number | null>, threshold: number): number[] {
    const n = values.length;
    if (threshold >= n) return Array.from({ length: n }, (_, i) => i);
    if (threshold < 3) return n ? [0, n - 1].slice(0, Math.max(1, threshold)) : [];
    const y = (i: number) => values[i] ?? 0;
    const kept = [0];
    const every = (n - 2) / (threshold - 2);
    let a = 0;
    for (let i = 0; i < threshold - 2; i++) {
        // The average of the next bucket is the third corner of each triangle.
        const nextStart = Math.floor((i + 1) * every) + 1, nextEnd = Math.min(Math.floor((i + 2) * every) + 1, n);
        let avgX = 0, avgY = 0;
        for (let j = nextStart; j < nextEnd; j++) { avgX += j; avgY += y(j); }
        const span = Math.max(1, nextEnd - nextStart); avgX /= span; avgY /= span;
        const start = Math.floor(i * every) + 1, end = Math.min(Math.floor((i + 1) * every) + 1, n - 1);
        let best = start, area = -1;
        for (let j = start; j < end; j++) {
            const size = Math.abs((a - avgX) * (y(j) - y(a)) - (a - j) * (avgY - y(a)));
            if (size > area) { area = size; best = j; }
        }
        kept.push(best); a = best;
    }
    kept.push(n - 1);
    return [...new Set(kept)].sort((p, q) => p - q);
}

/**
 * The points of a long trend to draw: each series keeps its own peaks and troughs within an equal share of
 * `limit`, and the shared axis keeps the union. Every kept point still carries its original row, so the tooltip
 * and drill read the row behind the point; `positions` are the kept points' places on the full axis.
 */
export function downsampleTrend<T extends { categories: string[]; series: Array<{ name: string; data: Array<number | null> }>; rowIndices: Array<Array<number | null>> }>(
    data: T, limit = MAX_TREND_POINTS): T & { positions?: number[] } {
    if (data.categories.length <= limit) return data;
    const share = Math.max(3, Math.floor(limit / Math.max(1, data.series.length)));
    const keep = [...new Set(data.series.flatMap(s => downsampleIndices(s.data, share)))].sort((p, q) => p - q);
    return { ...data, categories: keep.map(i => data.categories[i]), series: data.series.map(s => ({ ...s, data: keep.map(i => s.data[i]) })),
        rowIndices: data.rowIndices.map(rows => keep.map(i => rows[i] ?? null)), positions: keep };
}

function curveFor(points: number, series: Array<{ data: Array<number | null> }>): string {
    // Smoothing between sparse points invents peaks and troughs; only dense, complete series are smoothed,
    // and then monotonically so the line never overshoots a value.
    const gaps = series.some(s => s.data.some(v => v == null));
    return points <= 24 || gaps ? 'straight' : 'monotoneCubic';
}

/** Integer-only ticks for count axes (no "2.5 records"). */
function countAxis(maxValue: number): Record<string, unknown> {
    const extra: Record<string, unknown> = { decimalsInFloat: 0, forceNiceScale: true };
    if (maxValue > 0 && maxValue <= 5) { extra.tickAmount = Math.max(1, Math.ceil(maxValue)); extra.min = 0; extra.max = Math.ceil(maxValue); }
    return extra;
}

function renderApexPage(host: HTMLElement, sourceResult: QueryResult, kind: ChartKind, options: RenderOptions, context: SeriesContext): RenderedChart {
    const display = options.display ?? null;
    const dims = sourceResult.columns.filter((c) => c.kind === 'dimension');
    const measures = sourceResult.columns.filter((c) => c.kind === 'measure');
    if (dims.length === 0 || measures.length === 0) return renderTableInto(host, sourceResult, options);
    const circular = kind === 'donut' || kind === 'pie';
    // Time on the category axis: chronological, every bucket in the period present.
    const result = circular ? sourceResult : dims.length === 1 ? fillTimeGaps(sourceResult) : chronological(sourceResult);
    const drillable = options.onDrill ? drillableKeys(result) : [];
    const wrap = document.createElement('div');
    wrap.className = 'analytics-widget__chart-host';
    wrap.dataset.analyticsChartMount = '';

    const currencySuffix = context.currency ? ` (${context.currency})` : '';
    // Only money is named with the panel's currency; a count beside it is a plain number.
    const suffixFor = (measure: QueryResultColumn | undefined) => measure?.currencyAlias ? currencySuffix : '';
    const currencyRow = context.currency ? currencySample(result) : null;
    let mounted: MountedChart | null = null;

    if (circular) {
        const slices = pieSlices(result, display);
        if (slices.error) {
            const p = document.createElement('p'); p.setAttribute('role', 'status'); p.textContent = slices.error; host.appendChild(p);
            return { destroy() { p.remove(); } };
        }
        if (slices.note) host.appendChild(noteElement(slices.note));
        host.appendChild(wrap);
        const measure = measures[0];
        const colors = seriesColors(slices.labels);
        mounted = mountApex(wrap, ({ height, theme }) => ({
            ...baseOptions(kind, height, theme, slices.values.length),
            colors,
            series: slices.values,
            labels: slices.labels.map(l => l + suffixFor(measures[0])),
            dataLabels: { enabled: true, formatter: (percent: number, point: { seriesIndex?: number }) => slices.percentages
                ? `${percent.toFixed(percent < 10 ? 1 : 0)}%` : formatValue(slices.values[point?.seriesIndex ?? 0], measure, display, { row: currencyRow }) },
            legend: legendOptions(display, theme, height),
            tooltip: { theme: theme.mode, y: { title: { formatter: (name: string) => escapeHtml(String(name)) }, formatter: (_v: number, point: { seriesIndex?: number }) => {
                const index = point?.seriesIndex ?? 0; const rowIndex = slices.rows[index];
                const row = rowIndex == null ? currencyRow : result.rows[rowIndex];
                return escapeHtml(rowIndex == null ? formatValue(slices.values[index], measure, display, { row }) : displayCell(row?.[measure.alias] ?? null, measure, display, { row }));
            } } },
            chartEvents: drillable.length ? {
                dataPointSelection: (_e: unknown, _c: unknown, config: { dataPointIndex?: number }) => {
                    const row = slices.rows[config?.dataPointIndex ?? -1];
                    const key = row == null ? null : result.categoryKeys?.[row];
                    if (key) options.onDrill!(key);
                },
            } : undefined,
        }), { host, slot: wrap, height: context.height, observe: context.observe, label: chartAria(kind, measures, dims, drillable.length > 0) });
        return { destroy() { mounted?.destroy(); wrap.remove(); } };
    }

    host.appendChild(wrap);
    const axis = timeAxis(result);
    const buckets = axis && dims.length === 2 ? timeBuckets(result) : null;
    const withYear = axis ? spansYears(buckets?.values ?? result.rows.map(row => row[axis.alias])) : false;
    const full = cartesianData(result, { display, order: buckets?.values, withYear, fillMissing: !!axis });
    const trend = kind === 'line' || kind === 'area';
    // A trend over thousands of time buckets would draw tens of thousands of SVG nodes and take seconds per paint:
    // it draws representative points instead, each still mapped to its own row for the tooltip and drill.
    const data = trend && axis ? downsampleTrend(full) : full;
    const sampled = data !== full;
    const positions = sampled ? (data as ReturnType<typeof downsampleTrend<typeof full>>).positions ?? [] : [];
    if (sampled) host.insertBefore(noteElement(`This trend has ${full.categories.length.toLocaleString()} points. The line shows ${data.categories.length.toLocaleString()} of them, keeping every peak and trough; zoom in, or use the table view for every value.`), wrap);
    const names = data.series.map((s, index) => s.name + suffixFor(measures[data.measureIndices[index]]));
    const colors = seriesColors(names);
    const seriesMeasures = data.series.map((series, index) => ({ ...measures[data.measureIndices[index]], label: names[index] }));
    const points = data.categories.length;
    // Combo: each measure is a column or a line; axes still follow units, so the role never merges two units.
    const combo = kind === 'combo' ? comboLineAliases(measures, display?.lineMeasures) : null;
    const isLine = (index: number) => !!combo?.lines.has(measures[data.measureIndices[index]]?.alias);
    if (combo?.defaulted && display?.lineMeasures?.length)
        host.insertBefore(noteElement('A combo chart needs at least one column and one line, so the lines are chosen by unit.'), wrap);
    const zoom = trend && points > 25 && !options.noZoom;
    const allValues = data.series.flatMap(s => s.data).filter((v): v is number => v != null);
    const maxValue = allValues.length ? Math.max(...allValues.map(Math.abs)) : 0;
    const valueFormatter = (measure: QueryResultColumn, integer: boolean) => (value: number) =>
        integer && !Number.isInteger(Math.round(value * 1e9) / 1e9) ? '' : formatValue(value, measure, display, { row: currencyRow });
    const integerAxis = measures.every(isCount);

    mounted = mountApex(wrap, ({ height, theme }) => {
        const valueAxis = {
            labels: { style: { fontSize: '11px', colors: theme.foreColor }, formatter: valueFormatter(measures[0], integerAxis) },
            title: { text: display?.valueAxisLabel ?? undefined, style: { color: theme.mutedColor } },
            ...(integerAxis ? countAxis(maxValue) : {}),
        };
        // Time labels are short and must stay whole: overlapping ones are skipped rather than truncated.
        const categoryLabels = { style: { fontSize: '11px', colors: theme.foreColor }, rotate: 0, trim: !axis, hideOverlappingLabels: true,
            formatter: (value: unknown) => { const text = String(value ?? ''); return text.length > 28 ? `${text.slice(0, 27)}…` : text; } };
        const categoryAxis: Record<string, unknown> = { categories: data.categories, labels: categoryLabels,
            title: { text: display?.categoryAxisLabel ?? undefined, style: { color: theme.mutedColor } },
            tooltip: { enabled: false }, axisBorder: { color: theme.grid }, axisTicks: { color: theme.grid } };
        if (zoom) categoryAxis.tickAmount = Math.min(12, points);
        const opts: Record<string, unknown> = {
            ...baseOptions(kind, height, theme, points * data.series.length, zoom),
            colors,
            series: data.series.map((s, i) => combo ? { name: names[i], type: isLine(i) ? 'line' : 'column', data: s.data }
                // A sampled trend places each point at its own bucket on a numeric axis, so gaps between kept points keep their width.
                : { name: names[i], data: sampled ? s.data.map((y, j) => [positions[j], y]) : s.data }),
            dataLabels: { enabled: false },
            // A single series gets a legend only when the author asks for one; it then carries its currency.
            legend: legendOptions(display, theme, height, data.series.length > 1 || !!context.currency),
            tooltip: { theme: theme.mode, shared: !!combo, intersect: kind !== 'line' && kind !== 'area' && !combo,
                x: { formatter: (value: unknown, point?: { dataPointIndex?: number }) => escapeHtml(String(data.categories[point?.dataPointIndex ?? -1] ?? value ?? '')) },
                y: { title: { formatter: (name: string) => escapeHtml(String(name)) }, formatter: (_value: number | null, point: { seriesIndex?: number; dataPointIndex?: number }) => {
                    const index = data.rowIndices[point?.seriesIndex ?? 0]?.[point?.dataPointIndex ?? 0];
                    const measure = measures[data.measureIndices[point?.seriesIndex ?? 0] ?? 0];
                    if (index == null) {
                        // No row behind this point: no records, or a gap-filled zero for counts and sums on a time axis.
                        const own = data.series[point?.seriesIndex ?? 0]?.data[point?.dataPointIndex ?? 0];
                        return own == null ? 'No records' : escapeHtml(formatValue(own, measure, display, { row: currencyRow }));
                    }
                    const row = result.rows[index];
                    return escapeHtml(displayCell(row?.[measure.alias] ?? null, measure, display, { row }));
                } } },
            chartEvents: drillable.length ? (() => {
                // Bars and columns report a click as a data point selection. Line and area points are small and their
                // tooltip follows the nearest point, so Apex never selects them: a click inside the plot drills the point
                // the tooltip is showing. Both map the point to its own row; one click drills once.
                let last: { key: string; at: number } | null = null;
                const drill = (_event: unknown, _chartContext: unknown, config: { dataPointIndex?: number; seriesIndex?: number }) => {
                    const index = config?.dataPointIndex;
                    const row = index == null || index < 0 ? null : data.rowIndices[Math.max(0, config.seriesIndex ?? 0)]?.[index];
                    const key = row != null && row >= 0 ? result.categoryKeys?.[row] : null;
                    if (!key || (last && last.key === key && Date.now() - last.at < 300)) return;
                    last = { key, at: Date.now() };
                    options.onDrill!(key);
                };
                const events: Record<string, unknown> = { dataPointSelection: drill };
                if (trend || combo) events.click = (event: MouseEvent, chartContext: unknown, config: { dataPointIndex?: number; seriesIndex?: number }) => {
                    const plot = wrap.querySelector('.apexcharts-grid')?.getBoundingClientRect();
                    // Within the plot, allowing for a point drawn on its edge.
                    const edge = 8;
                    if (!plot || event.clientX < plot.left - edge || event.clientX > plot.right + edge || event.clientY < plot.top - edge || event.clientY > plot.bottom + edge) return;
                    drill(event, chartContext, config);
                };
                return events;
            })() : undefined,
        };
        if (kind === 'bar') {
            // Horizontal bars swap the axes: categories run down the side, values along the bottom.
            opts.xaxis = { ...categoryAxis, title: valueAxis.title, labels: { ...categoryLabels, formatter: valueAxis.labels.formatter },
                ...(integerAxis ? { decimalsInFloat: 0, ...(maxValue > 0 && maxValue <= 5 ? { tickAmount: Math.max(1, Math.ceil(maxValue)) } : {}) } : {}) };
            opts.yaxis = { labels: { ...categoryLabels, maxWidth: 180 }, title: categoryAxis.title };
            opts.plotOptions = { bar: { horizontal: true } };
        } else {
            // A category axis draws a label and a tick for every point; a sampled trend uses a numeric axis over the bucket
            // positions with about a dozen labelled ticks, each named by its bucket.
            const { categories: _categories, ...numericAxis } = categoryAxis;
            opts.xaxis = !sampled ? categoryAxis : { ...numericAxis, type: 'numeric', min: 0, max: full.categories.length - 1, tickAmount: 12,
                labels: { ...categoryLabels, formatter: (value: unknown) => categoryLabels.formatter(full.categories[Math.round(Number(value))] ?? '') } };
            opts.yaxis = seriesAxes(seriesMeasures, data.series.length, display, valueAxis, currencyRow);
        }
        if (trend) {
            opts.stroke = { width: 2, curve: curveFor(points, data.series) };
            // A split may occur in only one category. Its value still needs a visible,
            // selectable point even when there is no neighbouring value to join.
            const gaps = data.series.some(s => s.data.some(v => v == null));
            opts.markers = { size: points <= 100 || (gaps && !sampled) ? 3 : 0, strokeWidth: 1, hover: { size: 5 } };
        }
        if (kind === 'area') opts.fill = areaFill(colors);
        if (combo || options.zeroBaseline) {
            // Lines are drawn against a zero baseline like the columns beside them, so a small value never sits on the axis.
            if (Array.isArray(opts.yaxis) && allValues.every(v => v >= 0)) opts.yaxis = (opts.yaxis as Array<Record<string, unknown>>).map(a => ({ ...a, min: 0 }));
            else if (!Array.isArray(opts.yaxis) && allValues.every(v => v >= 0)) opts.yaxis = { ...(opts.yaxis as Record<string, unknown>), min: 0 };
        }
        if (combo) {
            opts.stroke = { width: data.series.map((_, i) => isLine(i) ? 3 : 0), curve: 'straight' };
            opts.markers = { size: data.series.map((_, i) => isLine(i) ? 4 : 0), strokeWidth: 1, hover: { size: 6 } };
            opts.plotOptions = { bar: { columnWidth: '55%' } };
        }
        return opts;
    }, { host, slot: wrap, height: context.height, observe: context.observe, label: chartAria(kind, measures, dims, drillable.length > 0) });
    return { destroy() { mounted?.destroy(); wrap.remove(); } };
}

function chartAria(kind: ChartKind, measures: QueryResultColumn[], dims: QueryResultColumn[], drillable: boolean): string {
    const what = `${chartLabel(kind)} chart of ${measures.map(m => m.label ?? m.alias).join(', ')} by ${dims.map(d => d.label ?? d.alias).join(' and ')}.`;
    return drillable ? `${what} Use View records to inspect a group.` : `${what} Use the table view for exact values.`;
}

/** Charts shorter than this drop the legend rather than squeeze the plot to nothing; tooltips still name each series. */
const LEGEND_MIN_HEIGHT = 180;

/**
 * Show legend applies to every chart that offers it. Left unset, a chart with one series has no legend (its title and
 * tooltip already name it) and every other chart has one; once the author ticks or clears the box, that choice wins,
 * including for a single series, which Apex would otherwise hide whatever the setting said.
 */
function legendOptions(display: QueryDisplay | null, theme: ChartTheme, height = Infinity, shownByDefault = true): Record<string, unknown> {
    const show = (display?.showLegend ?? shownByDefault) && height >= LEGEND_MIN_HEIGHT;
    return { position: 'bottom', show, showForSingleSeries: show, formatter: (name: string) => escapeHtml(String(name)),
        labels: { colors: theme.foreColor }, itemMargin: { horizontal: 8, vertical: 2 } };
}

/** Options every Apex chart in a report shares: size, theme, no inner offset, zoom for long trends. */
function baseOptions(kind: ChartKind, height: number, theme: ChartTheme, points: number, zoom = false): Record<string, unknown> {
    return {
        chart: {
            type: apexChartType(kind), width: '100%', height, stacked: kind === 'stackedColumn',
            // Apex otherwise adds 15px below the chart, which overflows a container sized to the chart.
            parentHeightOffset: 0,
            foreColor: theme.foreColor, fontFamily: theme.fontFamily, background: 'transparent',
            animations: { enabled: points <= 200, speed: 300 },
            toolbar: zoom ? { show: true, tools: { download: false, selection: false, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } } : { show: false },
            zoom: { enabled: zoom, type: 'x', autoScaleYaxis: true },
        },
        theme: { mode: theme.mode },
        grid: { borderColor: theme.grid, strokeDashArray: 4 },
    };
}

/**
 * Value axes for a cartesian chart. Measures that share a format share one axis; a measure with a
 * different format (a count beside a money total) gets its own axis on the opposite side.
 */
export function seriesAxes(measures: QueryResultColumn[], seriesCount: number, display: QueryDisplay | null | undefined,
    base: { labels: { style: Record<string, unknown> }; title: { text?: string } } & Record<string, unknown>, row: Record<string, unknown> | null = null): unknown {
    if (measures.length < 2 || seriesCount !== measures.length) return base;
    const signature = (m: QueryResultColumn) => { const f = resolveFormat(m, display); return `${unitSignature(m)}|${f.numberFormat}|${f.currency ?? ''}|${f.decimals ?? ''}|${f.abbreviate}`; };
    const groups = [...new Set(measures.map(signature))];
    if (groups.length < 2) return base;
    const owner = new Map(groups.map(g => [g, measures.find(m => signature(m) === g)!]));
    return measures.map((measure, index) => {
        const group = signature(measure); const first = owner.get(group)!;
        const integer = isCount(first);
        return {
            seriesName: first.label ?? first.alias,
            show: first === measure,
            opposite: groups.indexOf(group) > 0,
            title: { text: index === 0 ? base.title.text : undefined },
            ...(integer ? { decimalsInFloat: 0, forceNiceScale: true } : {}),
            labels: { style: base.labels.style, formatter: (value: number) => integer && !Number.isInteger(Math.round(value * 1e9) / 1e9) ? '' : formatValue(value, measure, display, { row }) },
        };
    });
}

export function chartNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const number = Number(value); return Number.isFinite(number) ? number : null;
}

export interface CartesianOptions {
    display?: QueryDisplay | null;
    /** Category values in axis order (e.g. every time bucket); categories not listed follow in first-seen order. */
    order?: unknown[];
    /** Day and week labels include the year. */
    withYear?: boolean;
    /** A missing split on a time axis is no records: zero for counts and sums. */
    fillMissing?: boolean;
}

/** Two dimensions split into category and series without summing, coercing missing cells, or merging null/empty keys. */
export function cartesianData(result: QueryResult, options: CartesianOptions = {}): { categories: string[]; series: Array<{ name: string; data: Array<number | null> }>; rowIndices: Array<Array<number | null>>; measureIndices: number[] } {
    const display = options.display ?? null;
    const dimensions = result.columns.filter(c => c.kind === 'dimension'); const measures = result.columns.filter(c => c.kind === 'measure');
    const context = (row: Record<string, unknown> | null): FormatContext => ({ row, withYear: options.withYear });
    if (dimensions.length === 2) {
        const categories = new Map<string, string>(); const groups = new Map<string, { name: string; value: unknown; rows: Map<string, number> }>();
        for (const value of options.order ?? []) categories.set(categoryKey(value), displayCell(value, dimensions[0], display, context(null)));
        result.rows.forEach((row, index) => {
            const key = categoryKey(row[dimensions[0].alias]); const groupKey = categoryKey(row[dimensions[1].alias]);
            if (!categories.has(key)) categories.set(key, displayCell(row[dimensions[0].alias], dimensions[0], display, context(row)));
            const group = groups.get(groupKey) ?? { name: displayCell(row[dimensions[1].alias], dimensions[1], display, context(row)), value: row[dimensions[1].alias], rows: new Map() };
            group.rows.set(key, index); groups.set(groupKey, group);
        });
        // A time split is ordered chronologically too.
        let entries = [...groups.values()];
        if (isTimeColumn(dimensions[1])) entries = entries.sort((a, b) => (timeValue(a.value) ?? Infinity) - (timeValue(b.value) ?? Infinity));
        const keys = [...categories.keys()];
        const missing = (measure: QueryResultColumn) => options.fillMissing ? emptyBucketValue(measure) : null;
        return { categories: [...categories.values()], series: entries.flatMap(group => measures.map(measure => ({ name: measures.length === 1 ? group.name : `${group.name} · ${measure.label ?? measure.alias}`,
            data: keys.map(key => group.rows.has(key) ? chartNumber(result.rows[group.rows.get(key)!][measure.alias]) : missing(measure)) }))),
            rowIndices: entries.flatMap(group => measures.map(() => keys.map(key => group.rows.get(key) ?? null))),
            measureIndices: entries.flatMap(() => measures.map((_, index) => index)) };
    }
    return { categories: result.rows.map(row => dimensions.map(d => displayCell(row[d.alias], d, display, context(row))).join(' · ')),
        series: measures.map(measure => ({ name: measure.label ?? measure.alias, data: result.rows.map(row => chartNumber(row[measure.alias])) })),
        rowIndices: measures.map(() => result.rows.map((_, index) => index)), measureIndices: measures.map((_, index) => index) };
}

function apexChartType(kind: ChartKind): string {
    switch (kind) {
        case 'line':           return 'line';
        case 'area':           return 'area';
        case 'donut':          return 'donut';
        case 'pie':            return 'pie';
        case 'heatmap':        return 'heatmap';
        case 'combo':          return 'line';
        case 'scatter':        return 'scatter';
        case 'treemap':        return 'treemap';
        case 'gauge':          return 'radialBar';
        case 'waterfall':      return 'rangeBar';
        default:               return 'bar';
    }
}

// ─────────────────────────── Heatmap ───────────────────────────

export type HeatCellState = 'value' | 'missing' | 'null';
export interface HeatCell { x: number; y: number; value: number | null; state: HeatCellState; row: Record<string, unknown> | null; rowIndex: number | null }
export interface HeatmapData { xLabels: string[]; yLabels: string[]; cells: HeatCell[][]; min: number | null; max: number | null }

function axisValues(result: QueryResult, column: QueryResultColumn): unknown[] {
    const seen = new Map<string, unknown>();
    for (const row of result.rows) { const key = categoryKey(row[column.alias]); if (!seen.has(key)) seen.set(key, row[column.alias]); }
    // The result's order is kept for real values; missing groups always come last, (Empty) before (No value).
    let values = [...seen.values()].filter(v => v != null && v !== '');
    const missing = [...seen.values()].filter(v => v === ''), absent = [...seen.values()].filter(v => v == null);
    values = [...values, ...missing, ...absent];
    if (isTimeColumn(column)) {
        // Time runs chronologically, with every bucket in between present.
        const buckets = timeBuckets({ ...result, columns: [column, ...result.columns.filter(c => c !== column)] });
        const dated = buckets?.values ?? values.filter(v => timeValue(v) != null).sort((a, b) => timeValue(a)! - timeValue(b)!);
        values = [...dated, ...values.filter(v => timeValue(v) == null)];
    }
    return values;
}

/**
 * The heatmap grid: every row holds every column, in the same order, so each cell sits under its own
 * column. A combination with no records and a combination whose value is missing are kept apart from zero.
 */
export function heatmapData(result: QueryResult, display: QueryDisplay | null = null): HeatmapData | null {
    const dims = result.columns.filter(c => c.kind === 'dimension');
    const measures = result.columns.filter(c => c.kind === 'measure');
    if (dims.length !== 2 || measures.length !== 1) return null;
    const [yDim, xDim] = dims; const measure = measures[0];
    const yValues = axisValues(result, yDim), xValues = axisValues(result, xDim);
    const yWithYear = isTimeColumn(yDim) && spansYears(yValues), xWithYear = isTimeColumn(xDim) && spansYears(xValues);
    const index = new Map<string, number>();
    result.rows.forEach((row, i) => index.set(`${categoryKey(row[yDim.alias])}|${categoryKey(row[xDim.alias])}`, i));
    let min: number | null = null, max: number | null = null;
    const cells = yValues.map((y, yi) => xValues.map((x, xi): HeatCell => {
        const rowIndex = index.get(`${categoryKey(y)}|${categoryKey(x)}`);
        if (rowIndex == null) return { x: xi, y: yi, value: null, state: 'missing', row: null, rowIndex: null };
        const row = result.rows[rowIndex]; const value = chartNumber(row[measure.alias]);
        if (value == null) return { x: xi, y: yi, value: null, state: 'null', row, rowIndex };
        min = min == null ? value : Math.min(min, value); max = max == null ? value : Math.max(max, value);
        return { x: xi, y: yi, value, state: 'value', row, rowIndex };
    }));
    return {
        xLabels: xValues.map(v => displayCell(v, xDim, display, { withYear: xWithYear })),
        yLabels: yValues.map(v => displayCell(v, yDim, display, { withYear: yWithYear })),
        cells, min, max,
    };
}

export interface HeatRange { from: number; to: number; color: string; name: string }

/** Colour scale: sequential for one-signed data, diverging around zero when values cross it. */
export function heatmapScale(min: number, max: number, base: string, negative: string, surface: string, format: (n: number) => string): HeatRange[] {
    const shades = (color: string, steps: number) => Array.from({ length: steps }, (_, i) => mixHex(color, surface, 0.82 - (0.6 * i) / Math.max(1, steps - 1)));
    const bands = (from: number, to: number, colors: string[]): HeatRange[] => {
        const width = (to - from) / colors.length;
        return colors.map((color, i) => {
            const a = from + width * i, b = i === colors.length - 1 ? to : from + width * (i + 1);
            return { from: a, to: b, color, name: `${format(a)} – ${format(b)}` };
        });
    };
    if (min === max) return [{ from: min, to: max, color: shades(base, 3)[1], name: format(min) }];
    if (min < 0 && max > 0) {
        const neg = bands(min, -1e-9, shades(negative, 3).reverse());
        const pos = bands(0, max, shades(base, 3));
        return [...neg, ...pos];
    }
    return bands(min, max, shades(min < 0 ? negative : base, 5).map((c, i, all) => min < 0 ? all[all.length - 1 - i] : c));
}

function renderHeatmap(host: HTMLElement, result: QueryResult, options: RenderOptions, context: SeriesContext): RenderedChart {
    const display = options.display ?? null;
    const dims = result.columns.filter((c) => c.kind === 'dimension');
    const measure = result.columns.find((c) => c.kind === 'measure')!;
    const grid = heatmapData(result, display);
    if (!grid) return renderTableInto(host, result, options);
    const pageX = 20, pageY = 25;
    let startX = 0, startY = 0;
    const pager = (grid.xLabels.length > pageX || grid.yLabels.length > pageY) ? document.createElement('div') : null;
    if (pager) { pager.className = 'analytics-chart-navigation'; pager.setAttribute('role', 'group'); pager.setAttribute('aria-label', 'Heatmap pages'); host.appendChild(pager); }
    const wrap = document.createElement('div'); wrap.className = 'analytics-widget__chart-host'; wrap.dataset.analyticsChartMount = '';
    host.appendChild(wrap);
    const drillable = options.onDrill ? drillableKeys(result) : [];
    const currencyRow = context.currency ? currencySample(result) : null;
    const compact: QueryDisplay = { ...(display ?? {}), columns: { ...(display?.columns ?? {}), [measure.alias]: { ...(display?.columns?.[measure.alias] ?? {}), abbreviate: true } } };
    const valueText = (cell: HeatCell) => cell.state === 'missing' ? 'No records' : cell.state === 'null' ? missingLabel(null)!
        : displayCell(cell.row![measure.alias], measure, display, { row: cell.row });
    let mounted: MountedChart | null = null;
    const paint = () => {
        mounted?.destroy();
        const xs = grid.xLabels.slice(startX, startX + pageX), ys = grid.yLabels.slice(startY, startY + pageY);
        const cells = grid.cells.slice(startY, startY + pageY).map(row => row.slice(startX, startX + pageX));
        if (pager) {
            const button = (label: string, disabled: boolean, onClick: () => void) => {
                const b = document.createElement('button'); b.type = 'button'; b.className = 'sp-button sp-button--neutral sp-button--sm';
                b.textContent = label; b.disabled = disabled; b.addEventListener('click', onClick); return b;
            };
            const status = document.createElement('span'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
            status.textContent = `Rows ${startY + 1}–${startY + ys.length} of ${grid.yLabels.length} · Columns ${startX + 1}–${startX + xs.length} of ${grid.xLabels.length}`;
            pager.replaceChildren(
                button('Previous rows', startY === 0, () => { startY = Math.max(0, startY - pageY); paint(); }),
                button('Next rows', startY + pageY >= grid.yLabels.length, () => { startY += pageY; paint(); }),
                status,
                button('Previous columns', startX === 0, () => { startX = Math.max(0, startX - pageX); paint(); }),
                button('Next columns', startX + pageX >= grid.xLabels.length, () => { startX += pageX; paint(); }));
        }
        const hasMissing = cells.some(r => r.some(c => c.state === 'missing'));
        const hasNull = cells.some(r => r.some(c => c.state === 'null'));
        const min = grid.min ?? 0, max = grid.max ?? 0;
        const span = Math.max(1, max - min);
        const MISSING = min - 2 * span - 10, NULL = MISSING - span - 10;
        mounted = mountApex(wrap, ({ height, theme }) => {
            const base = seriesColors([measure.label ?? measure.alias])[0];
            const ranges: HeatRange[] = heatmapScale(min, max, /^#/.test(base) ? base : themeColor('--color-primary', '--primary'), themeColor('--destructive'), theme.surface,
                n => formatValue(n, measure, compact, { row: currencyRow }));
            if (hasMissing) ranges.push({ from: MISSING, to: MISSING, color: mixHex(theme.grid, theme.surface, 0.35), name: 'No records' });
            if (hasNull) ranges.push({ from: NULL, to: NULL, color: mixHex(theme.mutedColor, theme.surface, 0.55), name: missingLabel(null)! });
            // Apex draws the first series at the bottom; reverse so the first row is on top.
            const series = cells.map((row, yi) => ({ name: ys[yi], data: row.map((cell, xi) => ({ x: xs[xi], y: cell.state === 'value' ? cell.value : cell.state === 'missing' ? MISSING : NULL })) })).reverse();
            const lookup = (seriesIndex: number, dataPointIndex: number): HeatCell | undefined => cells[cells.length - 1 - seriesIndex]?.[dataPointIndex];
            const labels = xs.length * ys.length <= 300;
            return {
                ...baseOptions('heatmap', height, theme, xs.length * ys.length),
                series,
                plotOptions: { heatmap: { enableShades: false, radius: 2, useFillColorAsStroke: false, colorScale: { ranges } } },
                stroke: { width: 1, colors: [theme.surface] },
                dataLabels: { enabled: labels, style: { colors: [theme.foreColor], fontSize: '11px', fontWeight: 500 },
                    formatter: (_v: number, point: { seriesIndex: number; dataPointIndex: number }) => {
                        const cell = lookup(point.seriesIndex, point.dataPointIndex);
                        return cell?.state === 'value' ? formatValue(cell.row![measure.alias], measure, compact, { row: cell.row }) : '';
                    } },
                legend: legendOptions(display, theme, height),
                xaxis: { type: 'category', categories: xs, labels: { style: { colors: theme.foreColor, fontSize: '11px' }, trim: true, rotate: -45, hideOverlappingLabels: false },
                    title: { text: dims[1].label ?? dims[1].alias, style: { color: theme.mutedColor } }, tooltip: { enabled: false } },
                yaxis: { labels: { style: { colors: [theme.foreColor], fontSize: '11px' }, maxWidth: 160 }, title: { text: dims[0].label ?? dims[0].alias, style: { color: theme.mutedColor } } },
                // The cell is named by its row and its column, as the drill choices name it ("Tech · Jan 2026"), then the
                // calculation and its value (ANX-0357).
                tooltip: { theme: theme.mode, custom: ({ seriesIndex, dataPointIndex }: { seriesIndex: number; dataPointIndex: number }) => {
                    const cell = lookup(seriesIndex, dataPointIndex); if (!cell) return '';
                    const title = `${ys[cells.length - 1 - seriesIndex] ?? ''} · ${xs[dataPointIndex] ?? ''}`;
                    return `<div class="apexcharts-tooltip-title">${escapeHtml(title)}</div><div class="analytics-chart-tooltip__body"><div>${escapeHtml(measure.label ?? measure.alias)}: <strong>${escapeHtml(valueText(cell))}</strong></div></div>`;
                } },
                chartEvents: drillable.length ? {
                    dataPointSelection: (_e: unknown, _c: unknown, config: { seriesIndex?: number; dataPointIndex?: number }) => {
                        const cell = lookup(config?.seriesIndex ?? -1, config?.dataPointIndex ?? -1);
                        const key = cell?.rowIndex != null ? result.categoryKeys?.[cell.rowIndex] : null;
                        if (key) options.onDrill!(key);
                    },
                } : undefined,
            };
        }, { host, slot: wrap, height: context.height, observe: context.observe,
            label: `Heatmap of ${measure.label ?? measure.alias} by ${dims[0].label ?? dims[0].alias} (rows) and ${dims[1].label ?? dims[1].alias} (columns). ${ys.length} rows and ${xs.length} columns; darker cells are higher. Use the table view for exact values.` });
    };
    paint();
    return { destroy() { mounted?.destroy(); wrap.remove(); pager?.remove(); } };
}

// ─────────────────────────── Scatter and bubble ───────────────────────────

/** Text colour that stays readable on a filled shape. */
function inkOn(fill: string, theme: ChartTheme): string {
    if (!/^#[0-9a-f]{6}$/i.test(fill)) return theme.foreColor;
    return inkOnFill(fill);
}

/** Category labels as unique strings: charts keyed by label would otherwise merge two groups that read the same. */
function uniqueLabels(labels: string[]): string[] {
    const seen = new Map<string, number>();
    return labels.map(label => {
        const n = seen.get(label) ?? 0; seen.set(label, n + 1);
        return n === 0 ? label : `${label}${'​'.repeat(n)}`;
    });
}

/** Axis bounds and tick count for numeric values: steps of 1, 2 or 5 × 10ⁿ, padded one step beyond the data. */
export function niceScale(values: number[], integer: boolean, ticks = 6): { min: number; max: number; tickAmount: number } {
    let lo = Math.min(...values), hi = Math.max(...values);
    if (!values.length || !Number.isFinite(lo) || !Number.isFinite(hi)) { lo = 0; hi = 1; }
    if (lo === hi) { lo -= 1; hi += 1; }
    const raw = (hi - lo) / ticks;
    const power = 10 ** Math.floor(Math.log10(raw));
    let step = [1, 2, 5, 10].map(f => f * power).find(s => s >= raw) ?? 10 * power;
    if (integer) step = Math.max(1, Math.round(step));
    let min = Math.floor(lo / step) * step, max = Math.ceil(hi / step) * step;
    // Points on the very edge are drawn half outside; pad by a step when a value sits on a bound (never below zero).
    if (min !== 0 && values.some(v => v === min)) min -= step;
    if (values.some(v => v === max)) max += step;
    return { min, max, tickAmount: Math.max(1, Math.round((max - min) / step)) };
}

function renderScatter(host: HTMLElement, result: QueryResult, options: RenderOptions, context: SeriesContext): RenderedChart {
    const display = options.display ?? null;
    const dims = result.columns.filter(c => c.kind === 'dimension');
    const { points, skipped, capped, bubble, measures } = scatterPoints(result, display);
    if (dims.length !== 1 || measures.length < 2) return renderTableInto(host, result, options);
    const notes: string[] = [];
    if (skipped) notes.push(`${skipped.toLocaleString()} ${skipped === 1 ? 'group is' : 'groups are'} not plotted because ${bubble ? 'a position or size is missing (or the size is negative)' : 'a position is missing'}.`);
    if (capped) notes.push(`Showing the first ${points.length.toLocaleString()} of ${(points.length + capped).toLocaleString()} groups. Add filters to plot the rest.`);
    if (result.truncated) notes.push('This result is partial (more groups match), so not every group is plotted.');
    for (const text of notes) host.appendChild(noteElement(text));
    if (!points.length) {
        const p = document.createElement('p'); p.setAttribute('role', 'status'); p.textContent = 'No group has values for both positions. Use the data table.'; host.appendChild(p);
        return { destroy() { p.remove(); } };
    }
    const wrap = document.createElement('div'); wrap.className = 'analytics-widget__chart-host'; host.appendChild(wrap);
    const drillable = options.onDrill ? drillableKeys(result) : [];
    const currencyRow = context.currency ? currencySample(result) : null;
    const suffix = (m: QueryResultColumn) => m.currencyAlias && context.currency ? ` (${context.currency})` : '';
    const axisTitle = (m: QueryResultColumn) => (m.label ?? m.alias) + suffix(m);
    const seriesName = dims[0].label ?? dims[0].alias;
    const color = seriesColors([seriesName])[0];
    const tick = (m: QueryResultColumn) => (value: number) => isCount(m) && !Number.isInteger(Math.round(value * 1e9) / 1e9) ? '' : formatValue(value, m, display, { row: currencyRow });
    const cell = (point: { rowIndex: number }, m: QueryResultColumn) => { const row = result.rows[point.rowIndex]; return displayCell(row?.[m.alias] ?? null, m, display, { row }); };
    // Nice bounds just beyond the data, so edge points are whole and count axes tick on whole numbers.
    const xScale = niceScale(points.map(p => p.x), isCount(measures[0])), yScale = niceScale(points.map(p => p.y), isCount(measures[1]));
    const mounted = mountApex(wrap, ({ height, theme }) => {
        const base = baseOptions('scatter', height, theme, points.length);
        (base.chart as Record<string, unknown>).type = bubble ? 'bubble' : 'scatter';
        (base.chart as Record<string, unknown>).zoom = { enabled: false };
        return {
            ...base,
            colors: [color],
            series: [{ name: seriesName, data: points.map(p => bubble ? [p.x, p.y, p.z] : [p.x, p.y]) }],
            dataLabels: { enabled: false },
            legend: { show: false },
            markers: { ...(bubble ? {} : { size: points.length > 500 ? 3 : 6 }), strokeColors: theme.surface, strokeWidth: 1, hover: { sizeOffset: 2 } },
            fill: { opacity: bubble ? 0.7 : 0.9 },
            plotOptions: { bubble: { minBubbleRadius: 4, maxBubbleRadius: 28 } },
            xaxis: { type: 'numeric', ...xScale, labels: { style: { fontSize: '11px', colors: theme.foreColor }, formatter: tick(measures[0]) },
                title: { text: display?.categoryAxisLabel ?? axisTitle(measures[0]), style: { color: theme.mutedColor } },
                tooltip: { enabled: false }, axisBorder: { color: theme.grid }, axisTicks: { color: theme.grid } },
            yaxis: { ...yScale, labels: { style: { fontSize: '11px', colors: theme.foreColor }, formatter: tick(measures[1]) },
                title: { text: display?.valueAxisLabel ?? axisTitle(measures[1]), style: { color: theme.mutedColor } } },
            tooltip: { theme: theme.mode, custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
                const point = points[dataPointIndex]; if (!point) return '';
                const lines = measures.map(m => `<div>${escapeHtml(axisTitle(m))}: <strong>${escapeHtml(cell(point, m))}</strong></div>`).join('');
                return `<div class="apexcharts-tooltip-title">${escapeHtml(point.label)}</div><div class="analytics-chart-tooltip__body">${lines}</div>`;
            } },
            chartEvents: drillable.length ? {
                dataPointSelection: (_e: unknown, _c: unknown, config: { dataPointIndex?: number }) => {
                    const point = points[config?.dataPointIndex ?? -1];
                    const key = point ? result.categoryKeys?.[point.rowIndex] : null;
                    if (key) options.onDrill!(key);
                },
            } : undefined,
        };
    }, { host, slot: wrap, height: context.height, observe: context.observe,
        label: `${bubble ? 'Bubble' : 'Scatter'} chart of ${seriesName}: ${measures[0].label ?? measures[0].alias} across, ${measures[1].label ?? measures[1].alias} up${bubble ? `, bubble size ${measures[2].label ?? measures[2].alias}` : ''}. ${points.length.toLocaleString()} points. ${drillable.length ? 'Use View records to inspect a group.' : 'Use the table view for exact values.'}` });
    return { destroy() { mounted.destroy(); wrap.remove(); } };
}

// ─────────────────────────── Waterfall ───────────────────────────

/** Increase, decrease and total colours: semantic tokens, kept distinct from each other. */
function waterfallColors(): Record<'increase' | 'decrease' | 'total' | 'empty', string> {
    const increase = themeColor('--success'), decrease = themeColor('--destructive');
    const palette = readPalette();
    const primary = themeColor('--color-primary', '--primary');
    const total = distinctPalette([increase, decrease, ...palette, primary]).find(c => c !== increase && c !== decrease) ?? primary;
    return { increase, decrease, total, empty: themeColor('--muted-foreground') };
}

function renderWaterfall(host: HTMLElement, sourceResult: QueryResult, options: RenderOptions, context: SeriesContext): RenderedChart {
    const display = options.display ?? null;
    const dims = sourceResult.columns.filter(c => c.kind === 'dimension');
    const measures = sourceResult.columns.filter(c => c.kind === 'measure');
    if (dims.length !== 1 || measures.length !== 1) return renderTableInto(host, sourceResult, options);
    const measure = measures[0];
    if (!waterfallMeasureOk(measure))
        return renderTableInto(host, sourceResult, options, `A waterfall adds each step to a running total, and ${measure.label ?? measure.alias} cannot be added up.`);
    // Time steps run chronologically, with empty periods as zero steps.
    const result = fillTimeGaps(sourceResult);
    const axis = timeAxis(result);
    const withYear = axis ? spansYears(result.rows.map(row => row[axis.alias])) : false;
    const labels = result.rows.map(row => displayCell(row[dims[0].alias], dims[0], display, { row, withYear }));
    const values = result.rows.map(row => chartNumber(row[measure.alias]));
    const { steps, combined } = waterfallSteps(labels, values, { showTotal: display?.showTotal !== false });
    if (combined) host.appendChild(noteElement(`The last ${combined.toLocaleString()} steps are combined into Other, so the running total is still complete.`));
    if (result.truncated) host.appendChild(noteElement('This result is partial (more groups match), so the running total covers the loaded groups only.'));
    const wrap = document.createElement('div'); wrap.className = 'analytics-widget__chart-host'; host.appendChild(wrap);
    const drillable = options.onDrill ? drillableKeys(result) : [];
    const currencyRow = context.currency ? currencySample(result) : null;
    const format = (n: number) => formatValue(n, measure, display, { row: currencyRow });
    const xs = uniqueLabels(steps.map(s => s.label));
    const kindsShown = (['increase', 'decrease', 'total'] as const).filter(k => steps.some(s => s.kind === k));
    const kindName = { increase: 'Increase', decrease: 'Decrease', total: 'Total', empty: 'No value' } as const;
    const name = (measure.label ?? measure.alias) + (measure.currencyAlias && context.currency ? ` (${context.currency})` : '');
    const signed = (step: WaterfallStep) => step.value == null ? missingLabel(null)! : step.kind === 'total' ? format(step.value) : `${step.value < 0 ? '−' : '+'}${format(Math.abs(step.value))}`;
    const integer = isCount(measure);
    const extent = steps.flatMap(s => [s.start, s.end]);
    const maxValue = Math.max(0, ...extent.map(Math.abs));
    const mounted = mountApex(wrap, ({ height, theme }) => {
        const colors = waterfallColors();
        return {
            ...baseOptions('waterfall', height, theme, steps.length),
            colors: [colors.total],
            series: [{ name, data: steps.map((s, i) => ({ x: xs[i], y: [Math.min(s.start, s.end), Math.max(s.start, s.end)], fillColor: colors[s.kind] })) }],
            plotOptions: { bar: { horizontal: false, columnWidth: steps.length > 30 ? '85%' : '60%' } },
            dataLabels: { enabled: steps.length <= 16 && height >= 200, offsetY: -2, background: { enabled: false },
                // Values sit inside their bars, so each label takes the ink that reads on its bar (zero-height steps sit on the surface).
                style: { fontSize: '11px', colors: [({ dataPointIndex }: { dataPointIndex: number }) => { const s = steps[dataPointIndex]; return s && s.start !== s.end ? inkOn(colors[s.kind], theme) : theme.foreColor; }] },
                formatter: (_v: unknown, point: { dataPointIndex: number }) => { const step = steps[point.dataPointIndex]; return step ? signed(step) : ''; } },
            stroke: { width: 0 },
            // A currency panel names its currency in the legend, like every other chart.
            legend: { ...legendOptions(display, theme, height), showForSingleSeries: true, customLegendItems: kindsShown.map(k => kindName[k] + (measure.currencyAlias && context.currency ? ` (${context.currency})` : '')),
                markers: { fillColors: kindsShown.map(k => colors[k]) } },
            xaxis: { type: 'category', labels: { style: { fontSize: '11px', colors: theme.foreColor }, rotate: 0, trim: !axis, hideOverlappingLabels: true,
                formatter: (value: unknown) => { const text = String(value ?? '').replace(/​+$/, ''); return text.length > 28 ? `${text.slice(0, 27)}…` : text; } },
                title: { text: display?.categoryAxisLabel ?? undefined, style: { color: theme.mutedColor } }, tooltip: { enabled: false },
                axisBorder: { color: theme.grid }, axisTicks: { color: theme.grid } },
            yaxis: { labels: { style: { fontSize: '11px', colors: theme.foreColor }, formatter: (v: number) => integer && !Number.isInteger(Math.round(v * 1e9) / 1e9) ? '' : format(v) },
                title: { text: display?.valueAxisLabel ?? undefined, style: { color: theme.mutedColor } }, ...(integer ? countAxis(maxValue) : {}),
                ...(integer && maxValue <= 5 && extent.some(v => v < 0) ? { min: Math.floor(Math.min(...extent)) } : {}) },
            annotations: extent.some(v => v < 0) ? { yaxis: [{ y: 0, borderColor: theme.mutedColor, strokeDashArray: 0 }] } : undefined,
            tooltip: { theme: theme.mode, custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
                const step = steps[dataPointIndex]; if (!step) return '';
                const body = step.kind === 'total' ? `<div>Total: <strong>${escapeHtml(format(step.end))}</strong></div>`
                    : `<div>Change: <strong>${escapeHtml(signed(step))}</strong></div><div>Running total: <strong>${escapeHtml(format(step.end))}</strong></div>`;
                return `<div class="apexcharts-tooltip-title">${escapeHtml(step.label)}</div><div class="analytics-chart-tooltip__body">${body}</div>`;
            } },
            chartEvents: drillable.length ? {
                dataPointSelection: (_e: unknown, _c: unknown, config: { dataPointIndex?: number }) => {
                    const row = steps[config?.dataPointIndex ?? -1]?.rowIndex;
                    const key = row != null ? result.categoryKeys?.[row] : null;
                    if (key) options.onDrill!(key);
                },
            } : undefined,
        };
    }, { host, slot: wrap, height: context.height, observe: context.observe,
        label: `Waterfall chart of ${measure.label ?? measure.alias} by ${dims[0].label ?? dims[0].alias}: ${steps.filter(s => s.kind !== 'total').length} steps${display?.showTotal !== false ? ` ending at a total of ${format(steps[steps.length - 1]?.end ?? 0)}` : ''}. Increases and decreases are coloured differently. ${drillable.length ? 'Use View records to inspect a group.' : 'Use the table view for exact values.'}` });
    return { destroy() { mounted.destroy(); wrap.remove(); } };
}

// ─────────────────────────── Treemap ───────────────────────────

function renderTreemap(host: HTMLElement, result: QueryResult, options: RenderOptions, context: SeriesContext): RenderedChart {
    const display = options.display ?? null;
    const dims = result.columns.filter(c => c.kind === 'dimension');
    const measure = result.columns.find(c => c.kind === 'measure');
    const data = treemapData(result, display);
    for (const text of data.notes) host.appendChild(noteElement(text));
    if (data.error || !measure) {
        const p = document.createElement('p'); p.setAttribute('role', 'status'); p.textContent = data.error ?? 'Use the data table.'; host.appendChild(p);
        return { destroy() { p.remove(); } };
    }
    const nested = dims.length === 2;
    const wrap = document.createElement('div'); wrap.className = 'analytics-widget__chart-host'; host.appendChild(wrap);
    const drillable = options.onDrill ? drillableKeys(result) : [];
    const currencyRow = context.currency ? currencySample(result) : null;
    const format = (n: number) => formatValue(n, measure, display, { row: currencyRow });
    const compact: QueryDisplay = { ...(display ?? {}), columns: { ...(display?.columns ?? {}), [measure.alias]: { ...(display?.columns?.[measure.alias] ?? {}), abbreviate: true } } };
    // Flat: every tile its own colour. Nested: one colour per outer group, shaded by value.
    const colors = nested ? seriesColors(data.series.map(s => s.name)) : seriesColors(data.series[0].data.map(t => t.x));
    const flatLabels = nested ? [] : uniqueLabels(data.series[0].data.map(t => t.x));
    const suffix = measure.currencyAlias && context.currency ? ` (${context.currency})` : '';
    const series = data.series.map(s => ({ name: nested ? s.name : s.name + suffix, data: s.data.map((t, i) => ({ x: nested ? t.x : flatLabels[i], y: t.y })) }));
    const tileAt = (seriesIndex: number, dataPointIndex: number) => data.series[seriesIndex]?.data[dataPointIndex];
    const share = (n: number) => data.total ? ` (${((n / data.total) * 100).toFixed(n / data.total < 0.1 ? 1 : 0)}%)` : '';
    const mounted = mountApex(wrap, ({ height, theme }) => ({
        ...baseOptions('treemap', height, theme, series.reduce((n, s) => n + s.data.length, 0)),
        colors,
        series,
        plotOptions: { treemap: { distributed: !nested, enableShades: nested, shadeIntensity: 0.35, useFillColorAsStroke: false } },
        stroke: { width: 2, colors: [theme.surface] },
        dataLabels: { enabled: true, style: { fontSize: '12px', fontWeight: 500, colors: colors.map(c => inkOn(c, theme)) },
            formatter: (text: string, point: { seriesIndex: number; dataPointIndex: number }) => {
                const tile = tileAt(point.seriesIndex, point.dataPointIndex);
                return tile ? [String(text).replace(/​+$/, ''), formatValue(tile.y, measure, compact, { row: currencyRow })] : String(text);
            } },
        legend: nested ? legendOptions(display, theme, height) : { show: false },
        tooltip: { theme: theme.mode, y: { title: { formatter: (name: string) => escapeHtml(String(name)) }, formatter: (_v: number, point: { seriesIndex: number; dataPointIndex: number }) => {
            const tile = tileAt(point.seriesIndex, point.dataPointIndex); if (!tile) return '';
            const row = tile.rowIndex == null ? null : result.rows[tile.rowIndex];
            return escapeHtml((row ? displayCell(row[measure.alias], measure, display, { row }) : format(tile.y)) + share(tile.y));
        } } },
        chartEvents: drillable.length ? {
            dataPointSelection: (_e: unknown, _c: unknown, config: { seriesIndex?: number; dataPointIndex?: number }) => {
                const tile = tileAt(config?.seriesIndex ?? -1, config?.dataPointIndex ?? -1);
                const key = tile?.rowIndex != null ? result.categoryKeys?.[tile.rowIndex] : null;
                if (key) options.onDrill!(key);
            },
        } : undefined,
    }), { host, slot: wrap, height: context.height, observe: context.observe,
        label: `Treemap of ${measure.label ?? measure.alias} by ${dims.map(d => d.label ?? d.alias).join(' within ')}: larger tiles are larger shares of ${format(data.total)}. ${drillable.length ? 'Use View records to inspect a group.' : 'Use the table view for exact values.'}` });
    return { destroy() { mounted.destroy(); wrap.remove(); } };
}

// ─────────────────────────── Gauge ───────────────────────────

const GAUGE_STATE_WORDS = { success: 'On track', warning: 'Warning', danger: 'Needs attention' } as const;

/**
 * One total against its target on a half-circle arc, toned by the thresholds in the measure's good direction.
 * Without a usable target or a single total, the single number is shown with what is missing.
 */
function renderGauge(host: HTMLElement, result: QueryResult, options: RenderOptions): RenderedChart {
    const display = options.display ?? result.display ?? null;
    const measures = result.columns.filter(c => c.kind === 'measure');
    const measure = measures[0];
    const target = options.goal ?? display?.target ?? null;
    const thresholds = options.thresholds ?? display?.thresholds ?? null;
    const fallback = (text: string | null): RenderedChart => {
        // The single number replaces the host's content; the title and notes already above it stay.
        const kept = typeof host.prepend === 'function' && host.children ? [...host.children] : [];
        const kpi = renderKpi(host, result, { ...options, goal: null, thresholds });
        if (kept.length) host.prepend(...kept);
        if (text && typeof host.insertAdjacentHTML === 'function') host.insertAdjacentHTML('beforeend', `<p class="analytics-chart-note" role="note">${escapeHtml(text)}</p>`);
        return kpi;
    };
    if (!measure || measures.length !== 1 || result.rows.length !== 1)
        return fallback(result.rows.length > 1 ? 'A gauge measures one total against a target; this report has several totals, so they are listed instead.' : null);
    const row = result.rows[0];
    const format = (n: number) => formatValue(n, measure, display, { row });
    const model = gaugeModel(chartNumber(row[measure.alias]), target, thresholds, format);
    if (!model) return fallback(target == null || !Number.isFinite(target) ? 'Set a target in Format to show progress on the gauge.'
        : chartNumber(row[measure.alias]) == null ? null : 'The gauge target must be above zero.');
    if (typeof document === 'undefined' || typeof document.createElement !== 'function' || typeof host.appendChild !== 'function') return fallback(null);
    const measureName = measure.label ?? measure.alias;
    const targetName = display?.targetLabel?.trim() || 'Target';
    const valueText = displayCell(row[measure.alias], measure, display, { row });
    const wrap = document.createElement('div'); wrap.className = 'analytics-widget__chart-host analytics-gauge';
    const meta = document.createElement('div'); meta.className = 'analytics-widget__goal-meta analytics-gauge__meta';
    const caption = document.createElement('span'); caption.className = 'analytics-widget__goal-caption'; caption.textContent = `${targetName}: ${model.progress.goalLabel}`;
    const status = document.createElement('span'); status.className = 'analytics-widget__goal-status'; status.textContent = model.progress.statusLabel;
    meta.append(caption, status);
    if (model.state) { const tone = document.createElement('span'); tone.className = `analytics-gauge__state analytics-kpi--${model.state}`; tone.textContent = GAUGE_STATE_WORDS[model.state]; meta.append(tone); }
    host.append(wrap, meta);
    const label = `Gauge of ${measureName}: ${valueText}, ${model.progress.pctLabel} (${targetName.toLowerCase()} ${model.progress.goalLabel}). ${model.progress.statusLabel}.${model.state ? ` ${GAUGE_STATE_WORDS[model.state]}.` : ''}`;
    const mounted = mountApex(wrap, ({ height, theme }) => {
        const tone = model.state === 'success' ? cssToken('--success') : model.state === 'warning' ? cssToken('--warning') : model.state === 'danger' ? cssToken('--destructive') : '';
        const color = tone ? toCssColor(tone) : seriesColors([measureName])[0];
        const base = baseOptions('gauge', height, theme, 1);
        // A half circle: sparkline mode lets the arc use the full width instead of reserving a whole circle.
        (base.chart as Record<string, unknown>).sparkline = { enabled: true };
        return {
            ...base,
            colors: [color],
            series: [Math.round(model.fill * 10) / 10],
            labels: [measureName],
            plotOptions: { radialBar: { startAngle: -90, endAngle: 90, hollow: { size: '62%' },
                track: { background: mixHex(theme.grid, theme.surface, 0.1), strokeWidth: '100%' },
                dataLabels: { name: { show: true, offsetY: 22, color: theme.mutedColor, fontSize: '12px', formatter: () => model.progress.pctLabel },
                    value: { show: true, offsetY: -14, color: theme.foreColor, fontSize: '24px', fontWeight: 700, formatter: () => valueText } } } },
            stroke: { lineCap: 'round' },
            legend: { show: false },
            tooltip: { enabled: false },
        };
    }, { host, slot: wrap, label });
    return { destroy() { mounted.destroy(); wrap.remove(); meta.remove(); } };
}

// ─────────────────────────── Image export ───────────────────────────

interface ChartPanel { chart: ApexInstance; mount: Element; title: string | null }

/** Every chart drawn in `host`, with the heading of its currency panel when money is drawn per currency. */
function chartPanels(host: Element): ChartPanel[] {
    const mounts = [...(host.matches?.('[data-analytics-chart-mount]') ? [host] : []), ...host.querySelectorAll('[data-analytics-chart-mount]')];
    return mounts.flatMap(mount => {
        const chart = chartInstance(mount);
        if (!chart) return [];
        const title = mount.closest('.analytics-chart-multiple')?.querySelector('.analytics-chart-multiple__title')?.textContent?.trim() || null;
        return [{ chart, mount, title }];
    });
}

function panelSvg(panel: ChartPanel): string | null {
    const svg = panel.chart.ctx?.exports?.getSvgString?.(1)
        ?? (() => { const el = panel.mount.querySelector('svg.apexcharts-svg'); return el ? new XMLSerializer().serializeToString(el) : null; })();
    return svg ? svg.replace(/^<\?xml[^>]*\?>\s*/, '') : null;
}

async function panelPng(panel: ChartPanel, scale: number): Promise<string | null> {
    const out = await panel.chart.dataURI?.({ scale });
    if (out?.imgURI) return out.imgURI;
    if (out?.blob) return URL.createObjectURL(out.blob);
    return null;
}

/** The first non-transparent background behind `element`, for the image's own background. */
function surfaceColor(element: Element): string {
    for (let node: Element | null = element; node; node = node.parentElement) {
        const color = getComputedStyle(node).backgroundColor;
        if (color && color !== 'transparent' && !/^rgba\(.*,\s*0\)$/.test(color)) return color;
    }
    return readChartTheme().surface;
}

const PANEL_TITLE = 24, PANEL_GAP = 16;
const escapeXml = (s: string): string => s.replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);

/** Several currency panels as one SVG: each panel under its currency heading, stacked. */
function stitchSvg(host: Element, panels: ChartPanel[]): string | null {
    const parts: string[] = []; let y = 0, width = 0;
    const ink = getComputedStyle(host).color || readChartTheme().foreColor;
    for (const panel of panels) {
        const svg = panelSvg(panel); if (!svg) continue;
        const rect = panel.mount.getBoundingClientRect();
        const w = Math.max(1, Math.ceil(rect.width)), h = Math.max(1, Math.ceil(rect.height));
        if (panel.title) {
            parts.push(`<text x="0" y="${y + 16}" font-family="sans-serif" font-size="14" font-weight="600" fill="${escapeXml(ink)}">${escapeXml(panel.title)}</text>`);
            y += PANEL_TITLE;
        }
        parts.push(`<g transform="translate(0,${y})">${svg}</g>`);
        y += h + PANEL_GAP; width = Math.max(width, w);
    }
    if (!parts.length) return null;
    const height = y - PANEL_GAP;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect width="100%" height="100%" fill="${escapeXml(surfaceColor(host))}"/>${parts.join('')}</svg>`;
}

/** Several currency panels as one PNG: each panel's own rendering under its heading, stacked on the surface colour. */
async function stitchPng(host: Element, panels: ChartPanel[], scale: number): Promise<string | null> {
    const images: Array<{ image: HTMLImageElement; title: string | null; url: string }> = [];
    for (const panel of panels) {
        const url = await panelPng(panel, scale); if (!url) continue;
        const image = new Image();
        await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error('chart image failed to load')); image.src = url; });
        images.push({ image, title: panel.title, url });
    }
    if (!images.length) return null;
    const title = PANEL_TITLE * scale, gap = PANEL_GAP * scale;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(...images.map(i => i.image.naturalWidth));
    canvas.height = images.reduce((sum, i) => sum + (i.title ? title : 0) + i.image.naturalHeight, 0) + gap * (images.length - 1);
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.fillStyle = surfaceColor(host); context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = getComputedStyle(host).color || readChartTheme().foreColor;
    context.font = `600 ${14 * scale}px sans-serif`; context.textBaseline = 'top';
    let y = 0;
    for (const { image, title: text, url } of images) {
        if (text) { context.fillText(text, 0, y + 2 * scale); y += title; }
        context.drawImage(image, 0, y); y += image.naturalHeight + gap;
        if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    }
    return canvas.toDataURL('image/png');
}

/**
 * The chart drawn inside `host` as an image data URL (PNG via Apex's own renderer, or SVG markup), or null
 * when the host shows no chart (tables, pivots, single numbers). Money drawn per currency is saved whole: every
 * currency panel, each under its heading, never just the first.
 */
export async function chartImageDataUrl(host: Element, format: 'png' | 'svg' = 'png', scale = 2): Promise<string | null> {
    const panels = chartPanels(host);
    if (!panels.length) return null;
    if (panels.length > 1) {
        if (format === 'png') return stitchPng(host, panels, scale);
        const stitched = stitchSvg(host, panels);
        return stitched ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(stitched)}` : null;
    }
    if (format === 'png') return panelPng(panels[0], scale);
    const svg = panelSvg(panels[0]);
    return svg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}` : null;
}

/** Whether `host` currently shows a chart that can be saved as an image. */
export function hasChartImage(host: Element): boolean {
    return chartPanels(host).length > 0;
}

/** Save the chart in `host` as `<fileName>.png` or `.svg`. Resolves false when there is no chart to save. */
export async function downloadChartImage(host: Element, format: 'png' | 'svg' = 'png', fileName = 'chart'): Promise<boolean> {
    const url = await chartImageDataUrl(host, format);
    if (!url) return false;
    const link = document.createElement('a');
    link.href = url; link.download = `${fileName.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'chart'}.${format}`;
    document.body.appendChild(link); link.click(); link.remove();
    if (url.startsWith('blob:')) setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return true;
}

// ─────────────────────────── Helpers ───────────────────────────

function categoryKey(value: unknown): string { return JSON.stringify([typeof value, value ?? null]); }

export { thresholdState } from './metric-chrome';
export { readChartTheme } from './chart-theme';

/** A row whose money carries the panel's currency (gap-filled periods carry none), for axis and legend formatting. */
function currencySample(result: QueryResult): Record<string, unknown> | null {
    const aliases = result.columns.filter(c => c.kind === 'measure' && c.currencyAlias).map(c => c.currencyAlias!);
    return result.rows.find(row => aliases.some(alias => row[alias] != null)) ?? result.rows[0] ?? null;
}
